import { record } from '@rrweb/record';
import type { listenerHandler } from '@rrweb/types';
import { CssSnapshotCapture, CSS_SNAPSHOT_TAG } from './cssSnapshot';
import { LocalAssetSnapshotCapture, LOCAL_ASSET_TAG } from './localAssetSnapshot';
import { FrictionClickDetector, type FrictionClickInfo } from './frictionClicks';
import { ContrastCapture, HB_CONTRAST_TAG, HB_CLIP_TAG, HB_BROKEN_ASSET_TAG, HB_OVERLAP_TAG, HB_MISALIGN_TAG } from './contrast';
import { v1 as uuidv1 } from 'uuid';
import { HumanBehaviorAPI } from './api';
import { RedactionManager, RedactionOptions, sanitizeUrl } from './redact';
import { logger, logError, logWarn, logInfo, logDebug, isSDKLogging } from './utils/logger';
import { PropertyManager, Properties } from './utils/property-manager';
import { BreadcrumbBuffer, BreadcrumbType } from './errors/breadcrumbs';
import { ErrorCapture } from './errors/capture';
import { ErrorFilterOptions } from './errors/filters';
import { ErrorMechanism, RequestContext } from './errors/error-payload';
import { redactBodyString, redactHeaders } from './errors/redact-network';
import { isHydrationError } from './errors/hydration';
import { Tracing, type HBSpan, type InactiveSpan } from './tracing';
import { isAdoptableSharedSession } from './shared-session';

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// Global declarations moved to browser-tracker.ts to avoid conflicts

export class HumanBehaviorTracker {
    private eventQueue: any[] = []; // Unified queue for all events (regular + recordings)
    private pendingCustomEvents: Array<{ eventName: string; properties: any; timestamp: number; eventId: string }> = [];
    private pendingLogs: Array<{ logData: any; timestamp: number }> = [];
    private pendingNetworkErrors: Array<{ errorData: any; timestamp: number }> = [];

    /**
     * Outbound custom-event batch queue. Debounced flush at
     * CUSTOM_EVENT_FLUSH_MS or hard-flush at CUSTOM_EVENT_BATCH_MAX. Mirrors
     * PostHog's 100ms / 50-event default. Preserves insertion order.
     */
    private customEventBatch: Array<{ eventName: string; eventProperties: any; eventId: string }> = [];
    private customEventBatchTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly CUSTOM_EVENT_FLUSH_MS = 100;
    private readonly CUSTOM_EVENT_BATCH_MAX = 50;
    
    private sessionId!: string;
    private windowId!: string; // Window ID for multi-window tracking
    // In-memory session state (source of truth during session)
    private _sessionActivityTimestamp: number | null = null;
    private _sessionStartTimestamp: number | null = null;
    private userProperties: Record<string, any> = {};
    private isProcessing: boolean = false;
    
    private flushInterval: number | null = null;
    // Two-tier flush cadence. Normally we batch every IDLE_FLUSH_INTERVAL_MS to
    // keep request volume low. When the server reports that a dashboard live
    // viewer is actively watching this project (via the `liveViewerActive` flag
    // on the /events response), we temporarily drop to LIVE_FLUSH_INTERVAL_MS so
    // the live replay feels real-time, then decay back after
    // LIVE_FLUSH_GRACE_MS without another positive signal. Dormant by default:
    // until a server sends the flag, behaviour is identical to the old fixed 3s.
    private readonly IDLE_FLUSH_INTERVAL_MS = 3000;
    private readonly LIVE_FLUSH_INTERVAL_MS = 150;
    private readonly LIVE_FLUSH_GRACE_MS = 6000;
    private flushTier: 'idle' | 'live' = 'idle';
    private liveFlushDeadline = 0;
    private readonly MAX_QUEUE_SIZE: number; // Configurable queue size

    // Fixed-cadence presence ping so the dashboard's "live now" indicator
    // counts idle pages too — flushEvents() short-circuits on an empty
    // queue, so without this an inert tab (no DOM mutations, no input)
    // would silently drop out of the live set after ~60s. 30s is well
    // under the server's LIVE_WINDOW_MS so a single missed beacon is
    // tolerated.
    private heartbeatInterval: number | null = null;
    private readonly HEARTBEAT_INTERVAL_MS = 30_000;

    // Idle window after which the SDK rotates `sessionId`.
    //
    // Session-continuity invariant across the three services (keep in sync):
    //   SDK idle rotation (here)          = 15 min
    //   ingestion stitch window           = 15 min  (SESSION_INACTIVE_MINUTES,
    //                                        ingestion-server/src/utils/sessionManager.ts)
    //   archiver finalize delay           = 20 min  (SESSION_INACTIVE_MINUTES,
    //                                        humanbehavior-session-archiver/src/archiver.ts)
    //
    // This value MUST EQUAL the ingestion stitch window so a returning user
    // within the window reuses the same sessionId on BOTH sides — rrweb chunks
    // and analytics events stitch onto one session and the replay covers the
    // full span. If the two drift, the dashboard shows a session "lasting 1h"
    // with a 20s replay: one side keeps the old id while the other starts a new
    // one, splitting rrweb from analytics. The archiver MUST trail both (20 > 15)
    // so it never finalizes a recording ingestion still considers live and then
    // drops the returning half as "already archived". (Tighter than the 30-min
    // GA/Mixpanel/Amplitude default — a deliberate trade to keep replay-archive
    // latency low.)
    private readonly SESSION_IDLE_TIMEOUT_MS = 15 * 60 * 1000;
    private readonly SESSION_MAX_LENGTH_MS = 24 * 60 * 60 * 1000;

    /** Only emit `$focus` blurred after blur lasts this long (quick alt-tab → no chop). */
    private focusBlurGraceTimeout: number | null = null;
    private readonly FOCUS_BLUR_GRACE_MS = 2_000;
    /** Last `$focus` marker we pushed (`null` = never blurred in this session tree). */
    private lastEmittedFocusState: 'focused' | 'blurred' | null = null;

    private api!: HumanBehaviorAPI;
    private endUserId: string | null = null;
    private apiKey!: string;
    private ingestionUrl!: string;
    private initialized: boolean = false;
    public initializationPromise: Promise<void> | null = null;
    private monthlyLimitReached: boolean = false;
    private redactionManager!: RedactionManager;
    private propertyManager!: PropertyManager;
    
    private isDomReady: boolean = false;
    private requestQueue: any[] = [];
    private domReadyHandlers: Array<() => void> = [];
    
    // Console tracking properties
    private originalConsole: {
        log: typeof console.log;
        warn: typeof console.warn;
        error: typeof console.error;
    } | null = null;
    private consoleTrackingEnabled: boolean = false;

    // Network tracking properties
    private originalFetch: typeof fetch | null = null;
    private networkTrackingEnabled: boolean = false;
    private captureRequestBodiesFlag: boolean = false;
    // Most recent network request (redacted), attached to the next error report
    // when it occurred within ERROR_REQUEST_WINDOW_MS. Holds failures (with
    // bodies/headers when opted in) and successes (metadata only).
    private lastRequestContext?: RequestContext;
    private lastRequestContextAt = 0; // epoch ms the lastRequestContext was recorded
    // Only correlate a request to an error if it happened within this window.
    private readonly ERROR_REQUEST_WINDOW_MS = 10_000;
    
    // Tracking configuration flags
    private enableConsoleTrackingFlag: boolean = true; // Default: enabled (opt-out)
    private enableNetworkTrackingFlag: boolean = true; // Default: enabled (opt-out)
    private enableErrorTrackingFlag: boolean = true; // Enable crash/error capture (default: true, set to false to opt-out)
    private enableWebVitalsFlag: boolean = true; // Enable Core Web Vitals tracking (FCP/LCP/CLS/INP/TTFB) (default: true, set to false to opt-out)
    private enableTracingFlag: boolean = true; // Enable distributed tracing (page-load + resource + custom spans) (default: true, opt-out)
    private tracing: Tracing | null = null;

    // Error (crash) capture properties
    private errorCapture: ErrorCapture | null = null;
    private errorFilterOptions: ErrorFilterOptions = {};
    private captureThirdPartyResourceErrorsFlag = false; // Default: first-party resource failures only
    private readonly breadcrumbs: BreadcrumbBuffer = new BreadcrumbBuffer();
    private release: string | null = null; // App version stamped on each error report
    private environment: string | null = null; // e.g. 'production' / 'staging'
    private commitSha: string | null = null; // Git commit SHA → GitHub blob links for issues
    private dist: string | null = null; // Build/dist discriminator → pairs with release to locate source maps

    // Navigation tracking properties
    public navigationTrackingEnabled: boolean = false;
    private currentUrl: string = '';
    private previousUrl: string = '';
    private originalPushState: typeof history.pushState | null = null;
    private originalReplaceState: typeof history.replaceState | null = null;
    private navigationListeners: Array<() => void> = [];
    /**
     * Timestamp of the last `pushState` call. Used by the `hashchange`
     * listener to suppress the dup pageview when a `pushState('/foo#bar')`
     * also fires `hashchange` synchronously.
     */
    private lastPushStateAt: number = 0;
    /** Window for hashchange-after-pushState dedupe. */
    private readonly NAVIGATION_DEDUPE_MS = 100;
    private _connectionBlocked: boolean = false;
    private recordInstance: listenerHandler | null = null;
    private sessionStartTime: number = Date.now();
    private rrwebRecord: any = null;
    private fullSnapshotTimeout: number | null = null;
    private cssSnapshotCapture: CssSnapshotCapture | null = null;
    private localAssetSnapshotCapture: LocalAssetSnapshotCapture | null = null;
    private contrastCapture: ContrastCapture | null = null;
    private brokenAssetHandler: ((e: Event) => void) | null = null;
    private brokenAssetSeen: Set<string> = new Set();
    private recordCanvas: boolean = false; // Store canvas recording preference
    private isStarted: boolean = false; // Guard against multiple start() calls
    private minimumDurationMilliseconds: number = 5000; // Default: 5 seconds minimum (configurable via init)
    
    // WindowId tracking properties
    private readonly _window_id_storage_key: string;
    private readonly _primary_window_exists_storage_key: string;
    
    // Idle detection properties
    private _isIdle: boolean | 'unknown' = 'unknown'; // Idle state: true, false, or 'unknown' (initial)
    private _lastActivityTimestamp: number = Date.now(); // Last user interaction timestamp
    private readonly IDLE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
    
    // Rage-click / dead-click detection. All thresholds and eligibility rules
    // live in frictionClicks.ts, which is a port of trace-compiler's
    // detectors so the raw $rageclick/$deadclick stream agrees with the
    // compiled rage_click/dead_click trace rows for the same session.
    private frictionClicks: FrictionClickDetector | null = null;
    private frictionMutationObserver: MutationObserver | null = null;
    
    /**
     * Check if the tracker has been started
     */
    public get isTrackerStarted(): boolean {
        return this.isStarted;
    }

    /**
     * DOM ready detection - more aggressive
     */
    private setupDomReadyHandler(): void {
        if (!isBrowser) {
            this.onDomReady();
            return;
        }

        // More aggressive DOM ready detection
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
            // DOM is ready enough
            this.onDomReady();
        } else if (document.addEventListener) {
            // Wait for DOMContentLoaded, but also check periodically
            const checkDomReady = () => {
                if (document.readyState === 'interactive' || document.readyState === 'complete') {
                    this.onDomReady();
                }
            };
            
            document.addEventListener('DOMContentLoaded', () => this.onDomReady(), { capture: false });
            
            // Also check periodically for faster response
            const interval = setInterval(() => {
                if (document.readyState === 'interactive' || document.readyState === 'complete') {
                    clearInterval(interval);
                    this.onDomReady();
                }
            }, 10); // Check every 10ms
            
            // Clear interval after 5 seconds to avoid infinite checking
            setTimeout(() => clearInterval(interval), 5000);
        } else {
            // Fallback for older browsers
            this.onDomReady();
        }
    }

    /**
     * Called when DOM is ready - processes queued requests
     */
    private onDomReady(): void {
        if (this.isDomReady) return; // Prevent multiple calls
        
        this.isDomReady = true;
        logDebug('🎯 DOM is ready, processing queued requests');
        
        // Process queued requests
        this.requestQueue.forEach(request => {
            this.processRequest(request);
        });
        this.requestQueue = [];
        
        // Call registered handlers
        this.domReadyHandlers.forEach(handler => handler());
        this.domReadyHandlers = [];
    }

    /**
     * Queue a request until DOM is ready
     */
    private queueRequest(request: any): void {
        if (this.isDomReady) {
            this.processRequest(request);
        } else {
            this.requestQueue.push(request);
        }
    }

    /**
     * Process a request (called after DOM is ready)
     */
    private async processRequest(request: any): Promise<void> {
        logDebug('Processing queued request:', request);
        
        switch (request.type) {
            case 'addEvent':
                await this.addEvent(request.event);
                break;
            case 'identifyUser':
                await this.identifyUser(request.userProperties, { identityToken: request.identityToken });
                break;
            case 'trackPageView':
                this.trackPageView();
                break;
            default:
                logWarn('Unknown request type:', request.type);
        }
    }

    /**
     * Register a handler to be called when DOM is ready
     */
    private registerDomReadyHandler(handler: () => void): void {
        if (this.isDomReady) {
            handler();
        } else {
            this.domReadyHandlers.push(handler);
        }
    }

    /**
     * Initialize the HumanBehavior tracker
     * This is the main entry point - call this once per page
     */
    public static init(apiKey: string, options?: {
        ingestionUrl?: string;
        logLevel?: 'none' | 'error' | 'warn' | 'info' | 'debug';
        redactFields?: string[]; // DEPRECATED: Use redactionStrategy instead
        redactionStrategy?: {
            mode: 'privacy-first' | 'visibility-first'; // Default: 'privacy-first'
            unredactFields?: string[]; // Fields to make visible (when mode: 'privacy-first')
            redactFields?: string[];   // Fields to hide (when mode: 'visibility-first')
        };
        enableAutomaticTracking?: boolean;
        suppressConsoleErrors?: boolean; // New option to control error suppression
        recordCanvas?: boolean; // Enable canvas recording with protection
        enableAutomaticProperties?: boolean; // Enable automatic property detection
        propertyDenylist?: string[]; // Properties to exclude from tracking
        automaticTrackingOptions?: {
            trackButtons?: boolean;
            trackLinks?: boolean;
            trackForms?: boolean;
            includeText?: boolean;
            includeClasses?: boolean;
        };
        maxQueueSize?: number; // Configurable queue size
        enableConsoleTracking?: boolean; // Enable console warn/error tracking (default: true, set to false to opt-out)
        enableNetworkTracking?: boolean; // Enable network error tracking (default: true, set to false to opt-out)
        enableErrorTracking?: boolean; // Enable crash/error capture (default: true, set to false to opt-out)
        ignoreErrors?: (string | RegExp)[]; // Error messages to drop client-side (substring or RegExp; Sentry semantics)
        denyUrls?: (string | RegExp)[]; // Drop errors originating from matching script URLs
        allowUrls?: (string | RegExp)[]; // When set, only keep errors originating from matching script URLs
        captureThirdPartyResourceErrors?: boolean; // Report failed loads of cross-origin resources (default: false, first-party only)
        release?: string; // App version/release stamped on each error report
        environment?: string; // Deployment environment (e.g. 'production')
        commitSha?: string; // Git commit SHA stamped on each error report (enables GitHub blob links)
        dist?: string; // Build/dist discriminator stamped on each error report (pairs with release for source-map lookup)
        captureRequestBodies?: boolean; // Opt-in: attach redacted request/response bodies + headers to error-correlated requests (default: false)
        enableWebVitals?: boolean; // Enable Core Web Vitals tracking (FCP/LCP/CLS/INP/TTFB) (default: true, set to false to opt-out)
        enableTracing?: boolean; // Enable distributed tracing (page-load/resource/custom spans) (default: true, set to false to opt-out)
        minimumDurationMilliseconds?: number; // Min session duration before events are sent (default: 5000). Set 0 to send immediately.
    }): HumanBehaviorTracker {
        // ✅ SUPPRESS COMMON RRWEB ERRORS FOR CLEAN CONSOLE
        if (isBrowser && options?.suppressConsoleErrors !== false) {
            // Suppress canvas security errors and network errors
            // Only patterns that are unambiguously OURS. The generic network and
            // CORS strings that used to be here ('CORS', 'Cross-Origin',
            // 'Failed to fetch', 'Failed to load resource',
            // 'Access-Control-Allow-Origin', 'net::ERR_BLOCKED_BY_CLIENT',
            // 'NetworkError…') are substrings of the HOST app's own messages, so
            // installing this SDK silently deleted a customer's console output:
            // someone debugging a production CORS incident saw an empty console
            // and concluded their code was fine. Our own failures already carry a
            // HumanBehavior marker, so nothing of ours needs the broad match.
            const originalConsoleError = console.error;
            console.error = (...args: any[]) => {
                const message = args.join(' ');
                if (
                    message.includes('SecurityError: Failed to execute \'toDataURL\'') ||
                    message.includes('Tainted canvases may not be exported') ||
                    message.includes('Cannot inline img src=') ||
                    message.includes('HumanBehavior ERROR') ||
                    message.includes('Failed to track custom event') ||
                    message.includes('Error sending custom event')
                ) {
                    // Silently suppress these common errors
                    return;
                }
                originalConsoleError.apply(console, args);
            };

            // Suppress console.warn for similar issues
            const originalConsoleWarn = console.warn;
            console.warn = (...args: any[]) => {
                const message = args.join(' ');
                if (
                    message.includes('Cannot inline img src=') ||
                    message.includes('Custom event network error') ||
                    message.includes('Request blocked by ad blocker')
                ) {
                    // Silently suppress these common warnings
                    return;
                }
                originalConsoleWarn.apply(console, args);
            };

            // Add global error handler for any remaining rrweb errors.
            // INTENTIONAL: this listener only swallows the narrow rrweb/canvas
            // console-noise messages matched below. It is SEPARATE from Issue
            // Detection's crash capture (ErrorCapture, installed in
            // setupErrorCapture()), which runs on its own `error` listener — so
            // genuine app crashes are still captured even though preventDefault()
            // is called here. Only fires when suppressConsoleErrors !== false.
            //
            // Narrowed for the same reason as the console patches above: matching
            // 'CORS' / 'NetworkError' / 'Failed to fetch' here also cancelled the
            // host app's own error events, hiding real failures from the customer.
            window.addEventListener('error', (event) => {
                const message = event.message || '';
                if (
                    message.includes('SecurityError') ||
                    message.includes('Tainted canvases') ||
                    message.includes('toDataURL')
                ) {
                    event.preventDefault();
                    return false;
                }
            });
        }
        // Return existing instance if already initialized
        if (isBrowser && (window as any).__humanBehaviorGlobalTracker) {
            logDebug('Tracker already initialized, returning existing instance');
            return (window as any).__humanBehaviorGlobalTracker;
        }

        // Configure logging if specified
        if (options?.logLevel) {
            // Class referenced explicitly so `init` also works when detached
            // from the class (e.g. aliased on a global object).
            HumanBehaviorTracker.configureLogging({ level: options.logLevel });
        }

        // Create new tracker instance
        const tracker = new HumanBehaviorTracker(apiKey, options?.ingestionUrl, {
            enableAutomaticProperties: options?.enableAutomaticProperties,
            propertyDenylist: options?.propertyDenylist,
            redactionStrategy: options?.redactionStrategy,
            redactFields: options?.redactFields,
            maxQueueSize: options?.maxQueueSize,
            enableConsoleTracking: options?.enableConsoleTracking,
            enableNetworkTracking: options?.enableNetworkTracking,
            enableErrorTracking: options?.enableErrorTracking,
            ignoreErrors: options?.ignoreErrors,
            denyUrls: options?.denyUrls,
            allowUrls: options?.allowUrls,
            captureThirdPartyResourceErrors: options?.captureThirdPartyResourceErrors,
            release: options?.release,
            environment: options?.environment,
            commitSha: options?.commitSha,
            dist: options?.dist,
            captureRequestBodies: options?.captureRequestBodies,
            enableWebVitals: options?.enableWebVitals,
            enableTracing: options?.enableTracing,
            minimumDurationMilliseconds: options?.minimumDurationMilliseconds
        });
        
        // Store canvas recording preference
        tracker.recordCanvas = options?.recordCanvas ?? false;
        
        // Set unredacted fields if specified (legacy support)
        if (options?.redactFields) {
            tracker.setUnredactedFields(options.redactFields);
        }

        // Handle new redaction strategy - this is now handled in the constructor
        // The redactionManager is created with the correct redactionStrategy in the constructor

        // Setup automatic tracking if enabled
        if (options?.enableAutomaticTracking !== false) {
            tracker.setupAutomaticTracking(options?.automaticTrackingOptions);
        }

        // Start tracking
        tracker.start();
        
        return tracker;
    }

    constructor(apiKey: string | undefined, ingestionUrl?: string, options?: {
        enableAutomaticProperties?: boolean;
        propertyDenylist?: string[];
        redactionStrategy?: {
            mode: 'privacy-first' | 'visibility-first';
            unredactFields?: string[];
            redactFields?: string[];
        };
        redactFields?: string[]; // Legacy support
        maxQueueSize?: number; // Configurable queue size
        enableConsoleTracking?: boolean; // Enable console warn/error tracking (default: true, set to false to opt-out)
        enableNetworkTracking?: boolean; // Enable network error tracking (default: true, set to false to opt-out)
        enableErrorTracking?: boolean; // Enable crash/error capture (default: true, set to false to opt-out)
        ignoreErrors?: (string | RegExp)[]; // Error messages to drop client-side (substring or RegExp; Sentry semantics)
        denyUrls?: (string | RegExp)[]; // Drop errors originating from matching script URLs
        allowUrls?: (string | RegExp)[]; // When set, only keep errors originating from matching script URLs
        captureThirdPartyResourceErrors?: boolean; // Report failed loads of cross-origin resources (default: false, first-party only)
        release?: string; // App version/release stamped on each error report
        environment?: string; // Deployment environment (e.g. 'production')
        commitSha?: string; // Git commit SHA stamped on each error report (enables GitHub blob links)
        dist?: string; // Build/dist discriminator stamped on each error report (pairs with release for source-map lookup)
        captureRequestBodies?: boolean; // Opt-in: attach redacted request/response bodies + headers to error-correlated requests (default: false)
        enableWebVitals?: boolean; // Enable Core Web Vitals tracking (FCP/LCP/CLS/INP/TTFB) (default: true, set to false to opt-out)
        enableTracing?: boolean; // Enable distributed tracing (page-load/resource/custom spans) (default: true, set to false to opt-out)
        minimumDurationMilliseconds?: number; // Min session duration before events are sent (default: 5000). Set 0 to send immediately.
    }) {
        if (!apiKey) {
            throw new Error('Human Behavior API Key is required');
        }
        
        // Initialize API
        //const defaultIngestionUrl = 'http://3.137.217.33:3000'; // AWS Development Server
        //const defaultIngestionUrl = 'http://ingestion-server-alb-1823866402.us-east-2.elb.amazonaws.com'; // ALB
        const defaultIngestionUrl = 'https://ingest.humanbehavior.co'; // HTTPS ALB
        const finalIngestionUrl = ingestionUrl || defaultIngestionUrl;
        this.api = new HumanBehaviorAPI({ 
            apiKey: apiKey,
            ingestionUrl: finalIngestionUrl
        });
        this.apiKey = apiKey;
        this.ingestionUrl = finalIngestionUrl;
        
        // Initialize queue size (default 1000)
        this.MAX_QUEUE_SIZE = options?.maxQueueSize ?? 1000;
        
        // Store tracking configuration flags (default: enabled, opt-out by setting to false)
        this.enableConsoleTrackingFlag = options?.enableConsoleTracking !== false; // Default: true (opt-out)
        this.enableNetworkTrackingFlag = options?.enableNetworkTracking !== false; // Default: true (opt-out)
        this.enableErrorTrackingFlag = options?.enableErrorTracking !== false; // Default: true (opt-out)
        this.errorFilterOptions = {
            ignoreErrors: options?.ignoreErrors,
            denyUrls: options?.denyUrls,
            allowUrls: options?.allowUrls,
        };
        this.captureThirdPartyResourceErrorsFlag = options?.captureThirdPartyResourceErrors === true;
        this.enableWebVitalsFlag = options?.enableWebVitals !== false; // Default: true (opt-out)
        this.enableTracingFlag = options?.enableTracing !== false; // Default: true (opt-out)

        // Optional version/environment context stamped onto every error report
        // and (via session properties below) onto analytics events / spans.
        this.release = options?.release ?? null;
        this.environment = options?.environment ?? null;
        this.commitSha = options?.commitSha ?? null;
        this.dist = options?.dist ?? null;
        // Opt-in: capture redacted request/response bodies for error-correlated requests
        this.captureRequestBodiesFlag = options?.captureRequestBodies === true;

        // Minimum session duration (ms) before buffered events are sent. Lets
        // callers (and tests) opt into immediate delivery with 0.
        if (typeof options?.minimumDurationMilliseconds === 'number' && options.minimumDurationMilliseconds >= 0) {
            this.minimumDurationMilliseconds = options.minimumDurationMilliseconds;
        }

        this.redactionManager = new RedactionManager({
            redactionStrategy: options?.redactionStrategy,
            legacyRedactFields: options?.redactFields // For backward compatibility
        });
        
        // Initialize property manager
        this.propertyManager = new PropertyManager({
            enableAutomaticProperties: options?.enableAutomaticProperties !== false,
            propertyDenylist: options?.propertyDenylist || []
        });
        // Stamp release/environment onto every analytics event so Monitoring
        // env filters (Releases CFR, Performance, Web Vitals) stay honest.
        if (this.release) {
            this.propertyManager.setSessionProperty('release', this.release);
        }
        if (this.environment) {
            this.propertyManager.setSessionProperty('environment', this.environment);
        }
        
        // DOM ready handling removed - using simpler approach

        // ✅ CLIENT-SIDE ID GENERATION
        // Generate endUserId locally (no server dependency)
        if (isBrowser) {
            const endUserIdKey = `human_behavior_end_user_id`;
            const existingEndUserId = this.getCookie(endUserIdKey);
            this.endUserId = existingEndUserId || uuidv1();
            if (!existingEndUserId) {
                this.setCookie(endUserIdKey, this.endUserId, 365);
                logDebug(`Generated new endUserId: ${this.endUserId}`);
            } else {
                logDebug(`Reusing existing endUserId: ${this.endUserId}`);
                }
        } else {
            this.endUserId = uuidv1();
        }

        // ✅ CLIENT-SIDE SESSION MANAGEMENT
        // Generate sessionId with timeout checking
        if (isBrowser) {
            // Initialize windowId storage keys
            const persistenceName = this.apiKey || 'default';
            this._window_id_storage_key = `human_behavior_${persistenceName}_window_id`;
            this._primary_window_exists_storage_key = `human_behavior_${persistenceName}_primary_window_exists`;
            
            this.sessionId = this.getOrCreateSessionId();
            this.windowId = this.getOrCreateWindowId(); // Multi-window tracking
            this.currentUrl = sanitizeUrl(window.location.href);
            (window as any).__humanBehaviorGlobalTracker = this;
            
            // Setup beforeunload listener to clear primary_window_exists flag
            this.setupWindowUnloadListener();
        } else {
            this._window_id_storage_key = '';
            this._primary_window_exists_storage_key = '';
            this.sessionId = uuidv1();
            this.windowId = uuidv1();
        }

        // ✅ SET TRACKING CONTEXT: Set session and user IDs for network error tracking
        this.api.setTrackingContext(this.sessionId, this.endUserId);

        // ✅ INITIALIZATION: Setup handlers immediately (no server call needed)
        this.initializationPromise = this.init().catch(error => {
            logError('Initialization failed:', error);
        });
    }

    private async init(): Promise<void> {
        try {
            // Mark initialized BEFORE wiring navigation tracking so the
            // synchronous initial-pageload `trackNavigationEvent('pageLoad')`
            // call doesn't get gated by the `if (!this.initialized) return`
            // guard. The previous order silently dropped every cold-start
            // pageview.
            this.initialized = true;

            // Setup handlers immediately
            if (isBrowser) {
                this.setupPageUnloadHandler();
                this.setupNavigationTracking();
            } else {
                logInfo('HumanBehaviorTracker initialized in server environment. Session tracking is disabled.');
            }

            logInfo(`HumanBehaviorTracker initialized with sessionId: ${this.sessionId}, endUserId: ${this.endUserId}`);
        } catch (error: any) {
            // Handle initialization errors gracefully - don't throw
            logError('Failed to initialize HumanBehaviorTracker:', error);
            this.initialized = true; // Allow tracker to work locally even if init fails
        }
    }

    /**
     * ✅ FIXED: Wait for Kafka-based initialization to complete
     */
    private async ensureInitialized(): Promise<void> {
        if (this.initializationPromise) {
            await this.initializationPromise;
        }
    }

    /**
     * Setup navigation event tracking for SPA navigation
     */
    private setupNavigationTracking(): void {
        if (!isBrowser || this.navigationTrackingEnabled) return;
        
        this.navigationTrackingEnabled = true;
        logDebug('Setting up navigation tracking');

        // Store original history methods
        this.originalPushState = history.pushState;
        this.originalReplaceState = history.replaceState;

        // Override pushState to capture programmatic navigation
        history.pushState = (...args) => {
            this.previousUrl = this.currentUrl;
            // Apply the original first so window.location reflects the new URL
            // before we read it (matches what listeners would observe).
            this.originalPushState!.apply(history, args);
            this.currentUrl = sanitizeUrl(window.location.href);
            this.lastPushStateAt = Date.now();

            this.trackNavigationEvent('pushState', this.previousUrl, this.currentUrl);
            this.takeFullSnapshot();
        };

        // Override replaceState to capture programmatic navigation
        history.replaceState = (...args) => {
            this.previousUrl = this.currentUrl;
            this.originalReplaceState!.apply(history, args);
            this.currentUrl = sanitizeUrl(window.location.href);
            this.lastPushStateAt = Date.now();

            this.trackNavigationEvent('replaceState', this.previousUrl, this.currentUrl);
            this.takeFullSnapshot();
        };

        // Listen for popstate events (back/forward navigation)
        const popstateListener = () => {
            this.previousUrl = this.currentUrl;
            this.currentUrl = sanitizeUrl(window.location.href);
            this.trackNavigationEvent('popstate', this.previousUrl, this.currentUrl);
            
            // Take FullSnapshot on navigation
            this.takeFullSnapshot();
        };
        
        window.addEventListener('popstate', popstateListener);
        this.navigationListeners.push(() => {
            window.removeEventListener('popstate', popstateListener);
        });

        // Listen for hashchange events. Suppress when a pushState fired in
        // the last NAVIGATION_DEDUPE_MS — that's the same logical
        // navigation (`pushState('/foo#bar')` triggers both).
        const hashchangeListener = () => {
            const now = Date.now();
            if (now - this.lastPushStateAt < this.NAVIGATION_DEDUPE_MS) {
                this.previousUrl = this.currentUrl;
                this.currentUrl = sanitizeUrl(window.location.href);
                return;
            }
            this.previousUrl = this.currentUrl;
            this.currentUrl = sanitizeUrl(window.location.href);
            this.trackNavigationEvent('hashchange', this.previousUrl, this.currentUrl);
        };
        
        window.addEventListener('hashchange', hashchangeListener);
        this.navigationListeners.push(() => {
            window.removeEventListener('hashchange', hashchangeListener);
        });

        // Track initial page load
        this.trackNavigationEvent('pageLoad', '', this.currentUrl);
    }

    /**
     * Track navigation events and send custom events
     */
    public async trackNavigationEvent(type: string, fromUrl: string, toUrl: string): Promise<void> {
        if (!this.initialized) return;

        // Same-URL dedupe — ignore programmatic SPA "navigations" that don't
        // actually change the URL (modal close handlers, focus listeners,
        // etc. routinely re-push the same path). PostHog dedupes by URL
        // across pushState/replaceState; we mirror that. `pageLoad` is
        // exempt because it's the canonical first pageview.
        if (type !== 'pageLoad' && fromUrl === toUrl) {
            logDebug(`Navigation dedupe: ${type} same URL (${toUrl})`);
            return;
        }

        try {
            // Send $pageview custom event for page loads and navigation.
            // Note: we used to also push a separate Type-5 rrweb "navigation"
            // event but that produced double accounting in dashboards that
            // counted both channels. The single $pageview is the source of
            // truth.
            if (type === 'pageLoad' || type === 'pushState' || type === 'replaceState' || type === 'popstate' || type === 'hashchange') {
                const pageViewProperties = {
                    // Full current URL minus credential-bearing params (see sanitizeUrl).
                    url: sanitizeUrl(window.location.href),
                    fromUrl: fromUrl,
                    // Per PostHog convention, expose the navigation cause
                    // as $navigation_type so dashboards can opt-out of
                    // shallow `replaceState` updates without losing the
                    // signal entirely. Keep `navigationType` as an alias
                    // for back-compat with anyone consuming the pre-0.7
                    // payload.
                    $navigation_type: type,
                    navigationType: type,
                    pathname: window.location.pathname,
                    search: window.location.search,
                    hash: window.location.hash,
                    referrer: document.referrer,
                    timestamp: Date.now()
                };

                await this.customEvent('$pageview', pageViewProperties);
            }

            logDebug(`Navigation tracked: ${type} from ${fromUrl} to ${toUrl}`);
        } catch (error) {
            logError('Failed to track navigation event:', error);
        }
    }

    public async trackPageView(url?: string): Promise<void> {
        if (!this.initialized) return;

        // Update automatic properties for new page
        this.propertyManager.updateAutomaticProperties();

        try {
            const pageViewData = {
                url: sanitizeUrl(url || window.location.href),
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
                referrer: document.referrer,
                timestamp: new Date().toISOString()
            };

            // Get enhanced properties with automatic properties
            const enhancedProperties = this.propertyManager.getEventProperties(pageViewData);

            // Add pageview event to the main event stream
            await this.addEvent({
                type: 5, // Custom event type
                data: {
                    payload: {
                        eventType: 'pageview',
                        ...enhancedProperties
                    }
                },
                timestamp: Date.now()
            });
            
            logDebug(`Pageview tracked: ${pageViewData.url}`);
        } catch (error) {
            logError('Failed to track pageview event:', error);
        }
    }

    public async customEvent(eventName: string, properties?: Record<string, any>): Promise<void> {
        // Reject empty/whitespace/null/undefined names client-side, matching
        // PostHog/Mixpanel. Sending these to the server only pollutes the
        // events table and downstream funnel queries.
        if (
            eventName == null ||
            typeof eventName !== 'string' ||
            eventName.trim() === ''
        ) {
            logWarn('customEvent() called with empty or invalid name; skipping');
            return;
        }

        // ✅ NON-BLOCKING: endUserId is always available (generated locally)
        // No need to wait for server initialization
        if (!this.endUserId) {
            // This should never happen, but fallback to anonymous if it does
            logWarn(`endUserId not available, using anonymous ID for event: ${eventName}`);
            this.endUserId = uuidv1();
        }

        // ✅ CHECK SESSION TIMEOUT before sending custom event (creates new session if expired)
        if (isBrowser) {
            this.checkAndRefreshSession();
        }

        // Feed click/navigation breadcrumbs so crash reports carry the
        // lead-up context. These two events are the chokepoint for user
        // clicks ($click) and SPA navigations ($pageview).
        if (eventName === '$click') {
            const label = properties?.text || properties?.elementText || properties?.tag || 'element';
            this.addBreadcrumb('click', String(label), {
                tag: properties?.tag,
                id: properties?.id,
                page: properties?.page
            });
        } else if (eventName === '$pageview') {
            this.addBreadcrumb('navigation', String(properties?.url || ''), {
                fromUrl: properties?.fromUrl
            });
        }

        // Get enhanced properties with automatic properties
        const enhancedProperties = this.propertyManager.getEventProperties(properties);

        // ✅ Check minimum duration - queue if below, send if above
        if (this.shouldSkipDueToMinimumDuration()) {
            logDebug(`Custom event '${eventName}' queued due to session duration below minimum`);
            this.pendingCustomEvents.push({
                eventName,
                properties: enhancedProperties,
                timestamp: Date.now(),
                // Idempotency ID minted at capture time so every delivery
                // attempt (batch, per-event fallback, beacon, retry) carries
                // the same ID and the server can drop duplicates.
                eventId: uuidv1()
            });
            return;
        }

        // ✅ Flush any pending custom events first (everything before 5 seconds)
        await this.flushPendingCustomEvents();

        // Route through the batch queue. PostHog batches up to 50 events per
        // 100ms; this is the same shape. Preserves insertion order.
        this.queueCustomEvent(eventName, enhancedProperties);
        logDebug(`Custom event queued: ${eventName}`, enhancedProperties);
    }

    /** Append to the outbound batch and arm/refresh the flush timer. */
    private queueCustomEvent(eventName: string, eventProperties: any, eventId?: string): void {
        // The idempotency ID is minted here (or inherited from the pending
        // queue) so retries and fallback transports all re-send the same ID.
        this.customEventBatch.push({ eventName, eventProperties, eventId: eventId ?? uuidv1() });

        // Page teardown path: events queued while the page is hidden (e.g.
        // CLS/INP, which the web-vitals library only finalizes on
        // visibilitychange/pagehide) would arm a debounce timer that never
        // fires — the page is gone before 100ms elapse. Our own pagehide
        // flush has usually already run by the time web-vitals reports, so
        // ship immediately via sendBeacon (survives navigation) instead of
        // batching. Without this, CLS and INP are silently lost on every
        // tab close / navigation.
        if (isBrowser && document.visibilityState === 'hidden') {
            this.flushCustomEventBatchBeacon();
            return;
        }

        if (this.customEventBatch.length >= this.CUSTOM_EVENT_BATCH_MAX) {
            void this.flushCustomEventBatch();
            return;
        }
        if (this.customEventBatchTimer == null) {
            this.customEventBatchTimer = setTimeout(() => {
                this.customEventBatchTimer = null;
                void this.flushCustomEventBatch();
            }, this.CUSTOM_EVENT_FLUSH_MS);
        }
    }

    /**
     * Teardown-path flush of the outbound custom-event batch: synchronous
     * sendBeacon (survives navigation), falling back to a keepalive fetch
     * when the beacon is unavailable or refuses the payload. Never awaited —
     * callers are unload/hidden handlers that cannot block.
     */
    private flushCustomEventBatchBeacon(): void {
        if (this.customEventBatchTimer != null) {
            clearTimeout(this.customEventBatchTimer);
            this.customEventBatchTimer = null;
        }
        if (this.customEventBatch.length === 0) return;
        const batch = this.customEventBatch;
        this.customEventBatch = [];
        const sent = this.api?.sendCustomEventBatchBeacon(
            this.sessionId,
            batch,
            this.endUserId
        );
        if (!sent) {
            // Beacon unavailable/refused: fall back to the keepalive path.
            this.api?.sendCustomEventBatch(this.sessionId, batch, this.endUserId).catch(() => {
                /* best-effort during teardown */
            });
        }
    }

    /**
     * Drain the sub-minimum-duration buffer into the outbound batch. The
     * minimum-duration gate is a delivery *delay*, not a filter — every event
     * in `pendingCustomEvents` is sent verbatim once the session crosses the
     * threshold. FullSnapshots bypass that gate entirely, so the replay
     * already exists server-side; a teardown that drops this buffer is what
     * produces replays with zero analytics events. Called from the hidden/
     * pagehide handlers right before the batch is beaconed out.
     */
    private drainPendingCustomEventsForTeardown(): void {
        if (this.pendingCustomEvents.length === 0) return;
        const pending = this.pendingCustomEvents;
        this.pendingCustomEvents = [];
        for (const ev of pending) {
            this.customEventBatch.push({
                eventName: ev.eventName,
                eventProperties: ev.properties,
                eventId: ev.eventId
            });
        }
    }

    /**
     * Flush the outbound custom-event batch. Falls back to per-event
     * `sendCustomEvent` (and finally to the rrweb event-stream Type-5
     * payload) when the batch endpoint errors — this is the same fallback
     * behaviour pre-0.7 had for individual sends.
     */
    private async flushCustomEventBatch(): Promise<void> {
        if (this.customEventBatchTimer != null) {
            clearTimeout(this.customEventBatchTimer);
            this.customEventBatchTimer = null;
        }
        if (this.customEventBatch.length === 0) return;
        const batch = this.customEventBatch;
        this.customEventBatch = [];

        try {
            await this.api.sendCustomEventBatch(this.sessionId, batch, this.endUserId);
            logDebug(`Custom event batch flushed: ${batch.length} event(s)`);
            return;
        } catch (error: any) {
            logError('Failed to flush custom event batch, falling back per-event:', error);
        }

        // Fallback path — try sending each event individually so a single
        // bad event doesn't drop the whole batch. Then fall further back to
        // the rrweb queue if that also fails.
        for (const ev of batch) {
            try {
                await this.api.sendCustomEvent(this.sessionId, ev.eventName, ev.eventProperties, this.endUserId, ev.eventId);
            } catch (perEventError: any) {
                logError('Per-event fallback also failed:', perEventError);
                try {
                    await this.addEvent({
                        type: 5,
                        data: {
                            payload: {
                                eventType: 'custom',
                                eventName: ev.eventName,
                                properties: ev.eventProperties || {},
                                timestamp: new Date().toISOString(),
                                url: isBrowser ? sanitizeUrl(window.location.href) : '',
                                pathname: isBrowser ? window.location.pathname : ''
                            }
                        },
                        timestamp: Date.now()
                    });
                } catch (fallbackError) {
                    logError('Failed to add custom event to event stream as fallback:', fallbackError);
                }
            }
        }
    }

    /**
     * Setup automatic tracking for buttons, links, and forms
     */
    private setupAutomaticTracking(options?: {
        trackButtons?: boolean;
        trackLinks?: boolean;
        trackForms?: boolean;
        includeText?: boolean;
        includeClasses?: boolean;
    }): void {
        if (!isBrowser) return;

        const config = {
            trackButtons: options?.trackButtons !== false,
            trackLinks: false, // Always disabled - only buttons and forms
            trackForms: options?.trackForms !== false,
            includeText: options?.includeText !== false,
            includeClasses: options?.includeClasses || false
        };

        logDebug('Setting up automatic tracking with config:', config);

        // Single autocapture handler replaces the old $click + $button_clicked
        // double-emission. Matches PostHog/Mixpanel autotrack: one $autocapture
        // event per user click, with element metadata as properties.
        this.setupAutocapture(config);

        // Setup form tracking
        if (config.trackForms) {
            this.setupAutomaticFormTracking(config);
        }

        this.setupFrictionClickDetection();
    }

    /**
     * PostHog/Mixpanel-style autocapture. One `$click` event per user click.
     * Walks up to the nearest interactive ancestor so a click on a <span>
     * inside a <button> is attributed to the button. The event name stays
     * constant (no per-label fragmentation) and text/role/etc. live as
     * properties.
     *
     * Note: the dashboard's hard-coded event name is `$click`, so we keep
     * that as the public event name. The shape is what changed (single
     * event, richer properties) — semantically this is "autocapture".
     */
    private setupAutocapture(config: {
        includeText?: boolean;
        includeClasses?: boolean;
    }): void {
        const INTERACTIVE_SELECTOR =
            'button, a, input, select, textarea, label, [role="button"], [role="link"], [role="tab"], [role="menuitem"]';

        document.addEventListener('click', async (event) => {
            const target = event.target as Element | null;
            // Robustness: clicks dispatched on `document` itself, on TextNodes
            // (e.g. inside contenteditable), or anywhere `target` is not an
            // Element would otherwise crash `.closest(...)` / `.tagName`.
            if (!target || target.nodeType !== 1 || typeof (target as any).closest !== 'function') {
                return;
            }

            const interactiveParent = (target as Element).closest(INTERACTIVE_SELECTOR);
            const element = (interactiveParent || target) as Element;
            const tagName = (element.tagName || '').toLowerCase();

            const properties: Record<string, any> = {
                tag: tagName,
                x: event.clientX,
                y: event.clientY,
                page: window.location.pathname,
                path: this.buildDomPath(element),
                timestamp: Date.now()
            };

            if (element.id) {
                properties.id = element.id;
                // Keep elementId for back-compat with anyone consuming the
                // pre-0.7 property names.
                properties.elementId = element.id;
            }

            const role = element.getAttribute && element.getAttribute('role');
            if (role) properties.role = role;

            const type = (element as HTMLInputElement).type;
            if (type && (tagName === 'input' || tagName === 'button')) {
                properties.type = type;
            }

            const href = (element as HTMLAnchorElement).href;
            if (href) properties.href = sanitizeUrl(href);

            if (config.includeText !== false) {
                const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
                if (text) {
                    properties.text = text.substring(0, 200);
                    // Keep elementText for back-compat.
                    properties.elementText = text.substring(0, 100);
                }
            }

            const className = (element as HTMLElement).className;
            if (config.includeClasses && typeof className === 'string' && className) {
                properties.class = className;
                properties.elementClass = className;
            }

            await this.customEvent('$click', properties);
        });
    }

    /**
     * Build a short DOM path like `body > div#root > button.cta`. Capped at
     * 8 ancestors so deeply-nested DOMs don't blow up the payload.
     */
    private buildDomPath(el: Element): string {
        const parts: string[] = [];
        let node: Element | null = el;
        let depth = 0;
        while (node && node.nodeType === 1 && depth < 8) {
            const tag = (node.tagName || '').toLowerCase();
            if (!tag) break;
            let segment = tag;
            if (node.id) segment += '#' + node.id;
            else if (typeof (node as HTMLElement).className === 'string' && (node as HTMLElement).className) {
                const cls = ((node as HTMLElement).className as string).trim().split(/\s+/)[0];
                if (cls) segment += '.' + cls;
            }
            parts.unshift(segment);
            if (tag === 'body' || tag === 'html') break;
            node = node.parentElement;
            depth++;
        }
        return parts.join(' > ');
    }

    /**
     * Wire the friction detectors: one click listener, plus the page-reaction
     * signals they need (mutations, scroll, typing, navigation).
     */
    private setupFrictionClickDetection(): void {
        if (!isBrowser) return;
        if (this.frictionClicks) return;

        const detector = new FrictionClickDetector({
            emit: (kind, info) => {
                void this.fireFrictionEvent(kind, info);
            },
        });
        this.frictionClicks = detector;

        document.addEventListener('click', (event: MouseEvent) => {
            const target = event.target as Element | null;
            // Synthetic clicks dispatched on `document`, on TextNodes, or on
            // detached nodes must never throw into the customer's handlers.
            if (!target || target.nodeType !== 1) return;
            detector.onClick(target, event.clientX, event.clientY);
        });

        // Any DOM change counts as the page responding. We don't care what
        // changed, only that something did.
        this.frictionMutationObserver = new MutationObserver(() => {
            detector.onReaction('dom');
        });
        this.frictionMutationObserver.observe(document, {
            attributes: true,
            characterData: true,
            childList: true,
            subtree: true
        });

        // Scroll and typing count for dead clicks only — a click that scrolled
        // the page (in-page anchor) or focused a field the user then typed into
        // did something. trace-compiler's rage detector ignores both.
        window.addEventListener('scroll', () => detector.onReaction('soft'), { capture: true, passive: true });
        document.addEventListener('input', () => detector.onReaction('soft'), { capture: true });

        // Navigation is the strongest possible reaction.
        const originalTrackNavigationEvent = this.trackNavigationEvent.bind(this);
        this.trackNavigationEvent = async (type: string, fromUrl: string, toUrl: string) => {
            detector.onReaction('dom');
            return originalTrackNavigationEvent(type, fromUrl, toUrl);
        };
        window.addEventListener('popstate', () => detector.onReaction('dom'));
        window.addEventListener('hashchange', () => detector.onReaction('dom'));
        window.addEventListener('beforeunload', () => detector.reset());
    }

    /**
     * Emit `$rageclick` / `$deadclick`. Property shape is load-bearing: the
     * dashboard, replay inspector and visitor timeline read `element`,
     * `elementId`, `elementClass`, `elementText`, `page` and `clickCount`.
     */
    private async fireFrictionEvent(kind: 'rage' | 'dead', info: FrictionClickInfo): Promise<void> {
        const element = info.node;
        const properties: Record<string, any> = {
            x: info.x,
            y: info.y,
            page: window.location.pathname,
            element: (element.tagName || '').toLowerCase(),
            clickCount: info.clickCount,
            timestamp: info.tsMs
        };
        if (info.occurrences !== undefined) {
            properties.occurrences = info.occurrences;
        }
        if (info.durationMs > 0) {
            properties.durationMs = info.durationMs;
        }
        if (element.id) {
            properties.elementId = element.id;
        }
        const className = (element as HTMLElement).className;
        if (typeof className === 'string' && className) {
            properties.elementClass = className;
        }
        if (element.textContent) {
            properties.elementText = element.textContent.trim().substring(0, 100);
        }
        Object.keys(properties).forEach(key => {
            if (properties[key] === null || properties[key] === undefined) {
                delete properties[key];
            }
        });

        // Names aligned with the dashboard's `event IN ('$rageclick',
        // '$deadclick')` queries (dead click was `$dead_click` pre-0.7).
        await this.customEvent(kind === 'rage' ? '$rageclick' : '$deadclick', properties);
    }

    /**
     * Setup automatic link tracking
     * TEMPORARILY DISABLED: Automatic custom event tracking
     */
    private setupAutomaticLinkTracking(config: {
        includeText?: boolean;
        includeClasses?: boolean;
    }): void {
        // TEMPORARILY DISABLED: Automatic custom event tracking
        return;
        
        // document.addEventListener('click', async (event) => {
        //     const target = event.target as HTMLElement;
            
        //     // Track link clicks
        //     if (target.tagName === 'A' || target.closest('a')) {
        //         const link = target.tagName === 'A'
        //             ? target as HTMLAnchorElement
        //             : target.closest('a') as HTMLAnchorElement;
                
        //         const properties: Record<string, any> = {
        //             linkUrl: link.href || null,
        //             linkId: link.id || null,
        //             linkTarget: link.target || null,
        //             page: window.location.pathname,
        //             timestamp: Date.now()
        //         };

        //         if (config.includeText) {
        //             properties.linkText = link.textContent?.trim() || null;
        //         }

        //         if (config.includeClasses) {
        //             properties.linkClass = link.className || null;
        //         }

        //         // Remove null values
        //         Object.keys(properties).forEach(key => {
        //             if (properties[key] === null) {
        //                 delete properties[key];
        //             }
        //         });

        //         await this.customEvent('link_clicked', properties);
        //     }
        // });
    }

    /**
     * Setup automatic form tracking
     */
    private setupAutomaticFormTracking(config: {
        includeText?: boolean;
        includeClasses?: boolean;
    }): void {
        document.addEventListener('submit', async (event) => {
            const form = event.target as HTMLFormElement;
            const formData = new FormData(form);
            
            const properties: Record<string, any> = {
                formId: form.id || null,
                formAction: form.action || null,
                formMethod: form.method || 'get',
                fields: Array.from(formData.keys()),
                page: window.location.pathname,
                timestamp: Date.now()
            };

            if (config.includeClasses) {
                properties.formClass = form.className || null;
            }

            // Remove null values
            Object.keys(properties).forEach(key => {
                if (properties[key] === null) {
                    delete properties[key];
                }
            });

            await this.customEvent('$form_submitted', properties);
        });
    }

    /**
     * Cleanup navigation tracking
     */
    private cleanupNavigationTracking(): void {
        if (!this.navigationTrackingEnabled) return;

        // Restore original history methods
        if (this.originalPushState) {
            history.pushState = this.originalPushState;
        }
        if (this.originalReplaceState) {
            history.replaceState = this.originalReplaceState;
        }

        // Remove event listeners
        this.navigationListeners.forEach(cleanup => cleanup());
        this.navigationListeners = [];

        this.navigationTrackingEnabled = false;
        logDebug('Navigation tracking cleaned up');
    }

    public static logToStorage(message: string) {
        logInfo(message);
    }

    /**
     * Configure logging behavior for the SDK
     * @param config Logger configuration options
     */
    public static configureLogging(config: { level?: 'none' | 'error' | 'warn' | 'info' | 'debug', enableConsole?: boolean, enableStorage?: boolean }) {
        const levelMap = {
            'none': 0,
            'error': 1,
            'warn': 2,
            'info': 3,
            'debug': 4
        };
        
        logger.setConfig({
            level: levelMap[config.level || 'error'],
            enableConsole: config.enableConsole !== false,
            enableStorage: config.enableStorage || false
        });
    }

    /**
     * Enable console event tracking
     */
    public enableConsoleTracking(): void {
        if (!isBrowser || this.consoleTrackingEnabled) return;
        
        // Store original console methods
        this.originalConsole = {
            log: console.log,
            warn: console.warn,
            error: console.error
        };

        // Override console methods to capture ALL console output (including logger output)
        console.log = (...args) => {
            this.trackConsoleEvent('log', args);
            this.originalConsole!.log(...args);
        };

        console.warn = (...args) => {
            this.trackConsoleEvent('warn', args);
            this.originalConsole!.warn(...args);
        };

        console.error = (...args) => {
            this.trackConsoleEvent('error', args);
            this.originalConsole!.error(...args);
        };

        this.consoleTrackingEnabled = true;
        logDebug('Console tracking enabled');
    }

    /**
     * Enable network error tracking by intercepting the global fetch API
     */
    public enableNetworkTracking(): void {
        if (!isBrowser || this.networkTrackingEnabled || typeof fetch === 'undefined') return;
        
        // Store original fetch
        this.originalFetch = window.fetch.bind(window);
        
        // Override global fetch to track network errors
        window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
            const requestStartTime = Date.now();
            const requestId = uuidv1();
            const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
            const method = (init?.method || (typeof input === 'object' && 'method' in input ? input.method : undefined) || 'GET').toUpperCase();
            
            // Check if we should skip tracking (SDK's own requests)
            const shouldSkipTracking = this.shouldSkipNetworkTracking(url);
            
            // Track long-loading requests (>10 seconds)
            const LONG_LOADING_THRESHOLD_MS = 10000; // 10 seconds
            let longLoadingTimeoutId: ReturnType<typeof setTimeout> | null = null;
            let longLoadingTracked = false;
            
            // Set up timeout to track long-loading requests
            if (!shouldSkipTracking) {
                longLoadingTimeoutId = setTimeout(() => {
                    const elapsedTime = Date.now() - requestStartTime;
                    if (!longLoadingTracked) {
                        longLoadingTracked = true;
                        const errorData = {
                            requestId,
                            url,
                            method,
                            status: null, // Request still in progress
                            statusText: null,
                            duration: elapsedTime,
                            timestampMs: Date.now(),
                            sessionId: this.sessionId,
                            endUserId: this.endUserId,
                            errorType: 'long_loading',
                            errorMessage: `Request took longer than ${LONG_LOADING_THRESHOLD_MS}ms (${elapsedTime}ms elapsed)`,
                            // New span fields
                            startTimeMs: requestStartTime,
                            spanName: `${method} ${url}`,
                            spanStatus: 'slow' as const,
                            attributes: {
                                'http.method': method,
                                'http.url': url,
                                'request.duration_ms': elapsedTime,
                                'request.long_loading_threshold_ms': LONG_LOADING_THRESHOLD_MS,
                            },
                            automaticProperties: this.propertyManager.getAutomaticProperties()
                        };
                        // ✅ Check minimum duration - queue if below, send if above
                        if (this.shouldSkipDueToMinimumDuration()) {
                            logDebug('Long-loading network error queued due to session duration below minimum');
                            this.pendingNetworkErrors.push({
                                errorData,
                                timestamp: Date.now()
                            });
                            return;
                        }
                        // ✅ Flush any pending network errors first (everything before 5 seconds)
                        this.flushPendingNetworkErrors();
                        this.api.sendNetworkError(errorData).catch(() => {}); // Non-blocking
                        return;
                    }
                }, LONG_LOADING_THRESHOLD_MS);
            }
            
            try {
                const response = await this.originalFetch!(input, init);
                const requestDuration = Date.now() - requestStartTime;
                
                // Clear long-loading timeout if request completed
                if (longLoadingTimeoutId) {
                    clearTimeout(longLoadingTimeoutId);
                }
                
                // Track failed requests (4xx, 5xx) AND skip SDK requests
                if (!response.ok && !shouldSkipTracking) {
                    const errorData = {
                        requestId,
                        url,
                        method,
                        status: response.status,
                        statusText: response.statusText,
                        duration: requestDuration,
                        timestampMs: Date.now(),
                        sessionId: this.sessionId,
                        endUserId: this.endUserId,
                        errorType: this.classifyHttpError(response.status),
                        errorMessage: response.statusText,
                        // New span fields
                        startTimeMs: requestStartTime,
                        spanName: `${method} ${url}`,
                        spanStatus: 'error' as const,
                        attributes: {
                            'http.status_code': response.status,
                            'http.status_text': response.statusText,
                        },
                        automaticProperties: this.propertyManager.getAutomaticProperties()
                    };
                    // ✅ Check minimum duration - queue if below, send if above
                    if (this.shouldSkipDueToMinimumDuration()) {
                        logDebug('Failed request network error queued due to session duration below minimum');
                        this.pendingNetworkErrors.push({
                            errorData,
                            timestamp: Date.now()
                        });
                        return response;
                    }
                    this.addBreadcrumb('network', `${errorData.method} ${errorData.url}`, { status: errorData.status, errorType: errorData.errorType });
                    let responseBodyText: string | undefined;
                    if (this.captureRequestBodiesFlag) {
                        try { responseBodyText = await response.clone().text(); } catch { /* body not readable */ }
                    }
                    this.lastRequestContext = this.buildRequestContext({
                        url, method, status: response.status, errorType: errorData.errorType, durationMs: requestDuration,
                        requestBody: init?.body, requestHeaders: this.headersToObject(init?.headers), responseBody: responseBodyText,
                    });
                    this.lastRequestContextAt = Date.now();
                    // ✅ Flush any pending network errors first (everything before 5 seconds)
                    this.flushPendingNetworkErrors();
                    this.api.sendNetworkError(errorData).catch(() => {}); // Non-blocking
                } else if (response.ok && !shouldSkipTracking) {
                    // Record the most recent SUCCESSFUL request (metadata only) so an
                    // error firing shortly after can be correlated to the API call that
                    // preceded it — e.g. a 200 returning malformed data → TypeError.
                    this.lastRequestContext = this.buildRequestContext({
                        url, method, status: response.status, durationMs: requestDuration,
                    });
                    this.lastRequestContextAt = Date.now();
                }
                
                return response;
            } catch (error: any) {
                const requestDuration = Date.now() - requestStartTime;
                
                // Clear long-loading timeout if request failed
                if (longLoadingTimeoutId) {
                    clearTimeout(longLoadingTimeoutId);
                }
                
                // Track network errors BUT skip SDK requests
                if (!shouldSkipTracking) {
                    const errorData = {
                        requestId,
                        url,
                        method,
                        status: null,
                        statusText: null,
                        duration: requestDuration,
                        timestampMs: Date.now(),
                        sessionId: this.sessionId,
                        endUserId: this.endUserId,
                        errorType: this.classifyNetworkError(error),
                        errorMessage: error.message,
                        errorName: error.name,
                        // New span fields
                        startTimeMs: requestStartTime,
                        spanName: `${method} ${url}`,
                        spanStatus: 'error' as const,
                        attributes: {
                            'error.name': error.name,
                            'error.message': error.message,
                        },
                        automaticProperties: this.propertyManager.getAutomaticProperties()
                    };
                    // ✅ Check minimum duration - queue if below, send if above
                    if (this.shouldSkipDueToMinimumDuration()) {
                        logDebug('Network error queued due to session duration below minimum');
                        this.pendingNetworkErrors.push({
                            errorData,
                            timestamp: Date.now()
                        });
                        throw error; // Re-throw to maintain error propagation
                    }
                    this.addBreadcrumb('network', `${errorData.method} ${errorData.url}`, { status: errorData.status, errorType: errorData.errorType });
                    this.lastRequestContext = this.buildRequestContext({
                        url, method, status: null, errorType: errorData.errorType, durationMs: requestDuration,
                        requestBody: init?.body, requestHeaders: this.headersToObject(init?.headers),
                    });
                    this.lastRequestContextAt = Date.now();
                    // ✅ Flush any pending network errors first (everything before 5 seconds)
                    this.flushPendingNetworkErrors();
                    this.api.sendNetworkError(errorData).catch(() => {}); // Non-blocking
                }
                
                throw error;
            }
        };

        // Mirror the fetch patch for XMLHttpRequest so XHR-based clients (axios's
        // XHR adapter, legacy code) get the same failed-request breadcrumbs +
        // sendNetworkError + requestContext. Prototype-patch (not constructor
        // replacement) to stay compatible with code that reads XHR statics.
        if (typeof XMLHttpRequest !== 'undefined') {
            const tracker = this;
            const xhrProto = XMLHttpRequest.prototype;
            const originalOpen = xhrProto.open;
            const originalSend = xhrProto.send;
            const originalSetRequestHeader = xhrProto.setRequestHeader;

            type HBXhrMeta = { method: string; url: string; headers: Record<string, string>; startTime: number; body?: unknown };

            xhrProto.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
                (this as unknown as { __hb?: HBXhrMeta }).__hb = {
                    method: String(method || 'GET').toUpperCase(),
                    url: typeof url === 'string' ? url : url.toString(),
                    headers: {},
                    startTime: 0,
                };
                return (originalOpen as (...a: unknown[]) => void).apply(this, [method, url, ...rest]);
            } as typeof xhrProto.open;

            xhrProto.setRequestHeader = function (this: XMLHttpRequest, name: string, value: string) {
                const meta = (this as unknown as { __hb?: HBXhrMeta }).__hb;
                if (meta) {
                    try { meta.headers[name] = value; } catch { /* ignore */ }
                }
                return originalSetRequestHeader.apply(this, [name, value] as [string, string]);
            } as typeof xhrProto.setRequestHeader;

            xhrProto.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
                const meta = (this as unknown as { __hb?: HBXhrMeta }).__hb;
                if (meta && !tracker.shouldSkipNetworkTracking(meta.url)) {
                    meta.startTime = Date.now();
                    meta.body = body;
                    this.addEventListener('loadend', function (this: XMLHttpRequest) {
                        try { tracker.handleXhrComplete(this, meta); } catch { /* never break the app */ }
                    });
                }
                return (originalSend as (...a: unknown[]) => void).apply(this, [body]);
            } as typeof xhrProto.send;
        }

        this.networkTrackingEnabled = true;
        logDebug('Network tracking enabled');
    }

    /**
     * Capture Core Web Vitals (FCP, LCP, CLS, INP, TTFB) via Google's
     * `web-vitals` library. Each metric is reported once with its FINAL value
     * (LCP/CLS/INP settle at page-hide) and emitted as a `$web_vitals` custom
     * event, so it flows through the existing batch → /customEvent/batch →
     * ClickHouse path with no backend change. See ADR-3.
     *
     * The library is loaded via dynamic import() so it stays off the initial
     * bundle and the critical render path. CLS/INP fire their final callback on
     * visibilitychange/pagehide and ride the SDK's existing batch-flush + beacon
     * fallback, so no extra unload handling is needed. Best-effort: a failed
     * import is swallowed, matching the other trackers.
     */
    public enableWebVitalsTracking(): void {
        if (!isBrowser) return;

        // One id per page load: web-vitals v4 reports each metric once for the
        // initial page load (no soft-nav), so all metrics emitted in this
        // page's lifetime share this id. A full reload re-runs the SDK and
        // mints a fresh id. This lets the dashboard stitch the per-metric
        // events back into a single page load and score it as a whole.
        const pageLoadId = uuidv1();

        import('web-vitals')
            .then(({ onFCP, onLCP, onCLS, onINP, onTTFB }) => {
                const report = (metric: {
                    name: string;
                    value: number;
                    rating: string;
                    id: string;
                    navigationType: string;
                }): void => {
                    void this.customEvent('$web_vitals', {
                        $web_vitals_metric: metric.name, // FCP | LCP | CLS | INP | TTFB
                        $web_vitals_value: metric.value,
                        [`$web_vitals_${metric.name}_value`]: metric.value,
                        $web_vitals_rating: metric.rating, // good | needs-improvement | poor
                        $web_vitals_id: metric.id,
                        $web_vitals_navigation_type: metric.navigationType,
                        $web_vitals_pageload_id: pageLoadId,
                    });
                };

                onFCP(report);
                onLCP(report);
                onCLS(report);
                onINP(report);
                onTTFB(report);

                logDebug('Web Vitals tracking enabled');
            })
            .catch((error) => {
                logWarn('Failed to load web-vitals; Web Vitals tracking disabled:', error);
            });
    }

    /**
     * Enable distributed tracing: a page-load transaction with navigation-phase
     * + resource child spans, custom spans via startSpan/startInactiveSpan, and
     * trace-context header propagation on same-origin requests. Spans batch
     * through `api.sendSpans` (and sendBeacon on unload). Best-effort.
     */
    public enableTracing(): void {
        if (!isBrowser || this.tracing) return;
        this.tracing = new Tracing({
            getSession: () => ({
                sessionId: this.sessionId,
                endUserId: this.endUserId,
                automaticProperties: this.propertyManager.getAutomaticProperties(),
                release: this.release,
                environment: this.environment,
            }),
            sendSpans: (spans: HBSpan[], ctx, useBeacon) => {
                if (useBeacon) {
                    const ok = this.api.sendSpansBeacon(spans, ctx);
                    if (!ok) void this.api.sendSpans(spans, ctx);
                } else {
                    void this.api.sendSpans(spans, ctx);
                }
            },
            shouldSkipUrl: (url: string) => this.shouldSkipNetworkTracking(url),
        });
        this.tracing.start();
        logDebug('Distributed tracing enabled');
    }

    /**
     * Time a synchronous/async callback as a tracing span (child of the current
     * page-load trace). No-op (still runs the callback) if tracing is disabled.
     */
    public startSpan<T>(
        opts: { name: string; op?: string; attributes?: Record<string, unknown> },
        callback: () => T,
    ): T {
        if (!this.tracing) return callback();
        return this.tracing.startSpan(opts, callback);
    }

    /**
     * Open a tracing span the caller ends manually (`span.end()`). Returns a
     * no-op span if tracing is disabled.
     */
    public startInactiveSpan(opts: {
        name: string;
        op?: string;
        attributes?: Record<string, unknown>;
    }): InactiveSpan {
        if (this.tracing) return this.tracing.startInactiveSpan(opts);
        return {
            setAttribute() {},
            setStatus() {},
            end() {},
        };
    }

    /**
     * Handle a completed (failed) XHR: record a breadcrumb, capture redacted
     * requestContext, and ship a network-error report — mirroring the fetch path.
     */
    private handleXhrComplete(
        xhr: XMLHttpRequest,
        meta: { method: string; url: string; headers: Record<string, string>; startTime: number; body?: unknown },
    ): void {
        const status = xhr.status;
        // 2xx/3xx are successes; status 0 is a network error/blocked request.
        if (status >= 200 && status < 400) {
            // Record the most recent SUCCESSFUL request (metadata only) for
            // error correlation, mirroring the fetch success path.
            this.lastRequestContext = this.buildRequestContext({
                url: meta.url, method: meta.method, status, durationMs: Date.now() - meta.startTime,
            });
            this.lastRequestContextAt = Date.now();
            return;
        }
        const duration = Date.now() - meta.startTime;
        const isNetworkError = status === 0;
        const errorType = isNetworkError ? 'network_error' : this.classifyHttpError(status);
        let responseBody: string | undefined;
        try {
            if (typeof xhr.responseText === 'string') {
                responseBody = xhr.responseText;
            }
        } catch { /* responseText throws for non-text responseType */ }

        const errorData = {
            requestId: uuidv1(),
            url: meta.url,
            method: meta.method,
            status: isNetworkError ? null : status,
            statusText: xhr.statusText || null,
            duration,
            timestampMs: Date.now(),
            sessionId: this.sessionId,
            endUserId: this.endUserId,
            errorType,
            errorMessage: xhr.statusText || (isNetworkError ? 'Network request failed' : `HTTP ${status}`),
            startTimeMs: meta.startTime,
            spanName: `${meta.method} ${meta.url}`,
            spanStatus: 'error' as const,
            attributes: {
                'http.method': meta.method,
                'http.url': meta.url,
                'http.status_code': status,
            },
            automaticProperties: this.propertyManager.getAutomaticProperties(),
        };

        if (this.shouldSkipDueToMinimumDuration()) {
            this.pendingNetworkErrors.push({ errorData, timestamp: Date.now() });
            return;
        }
        this.addBreadcrumb('network', `${meta.method} ${meta.url}`, { status: errorData.status, errorType });
        this.lastRequestContext = this.buildRequestContext({
            url: meta.url, method: meta.method, status: errorData.status, errorType, durationMs: duration,
            requestBody: meta.body, requestHeaders: meta.headers, responseBody,
        });
        this.lastRequestContextAt = Date.now();
        this.flushPendingNetworkErrors();
        this.api.sendNetworkError(errorData).catch(() => {});
    }

    /**
     * Build a redacted requestContext for an error-correlated request. Metadata
     * (url/method/status) is always included; bodies + headers only when the host
     * opted in via `captureRequestBodies`.
     */
    private buildRequestContext(raw: {
        url: string;
        method: string;
        status: number | null;
        errorType?: string;
        durationMs?: number;
        requestBody?: unknown;
        requestHeaders?: Record<string, string>;
        responseBody?: string;
    }): RequestContext {
        const ctx: RequestContext = {
            url: raw.url,
            method: raw.method,
            status: raw.status,
            errorType: raw.errorType,
            durationMs: raw.durationMs,
        };
        if (this.captureRequestBodiesFlag) {
            if (raw.requestHeaders && Object.keys(raw.requestHeaders).length > 0) {
                ctx.requestHeaders = redactHeaders(raw.requestHeaders);
            }
            if (typeof raw.requestBody === 'string' && raw.requestBody) {
                ctx.requestBody = redactBodyString(raw.requestBody);
            }
            if (typeof raw.responseBody === 'string' && raw.responseBody) {
                ctx.responseBody = redactBodyString(raw.responseBody);
            }
        }
        return ctx;
    }

    /** Normalize a fetch HeadersInit into a plain object for redaction. */
    private headersToObject(headers?: HeadersInit): Record<string, string> | undefined {
        if (!headers) {
            return undefined;
        }
        const out: Record<string, string> = {};
        try {
            if (typeof Headers !== 'undefined' && headers instanceof Headers) {
                headers.forEach((value, key) => { out[key] = value; });
            } else if (Array.isArray(headers)) {
                for (const [key, value] of headers) {
                    out[key] = value;
                }
            } else {
                for (const [key, value] of Object.entries(headers)) {
                    out[key] = String(value);
                }
            }
        } catch {
            return undefined;
        }
        return out;
    }

    /**
     * Setup crash/error capture.
     *
     * Installs listeners for uncaught errors (`window.onerror`) and unhandled
     * promise rejections, then ships a structured report via `api.sendError`.
     * The heavy lifting (stack parsing, breadcrumbs, dedup) lives in the pure
     * helpers under `./errors`; this just supplies the live session context.
     *
     * Note: this is separate from the rrweb console-noise `preventDefault()`
     * filter installed during construction — that filter only suppresses the
     * SDK's own internal noise from the console and does not stop genuine app
     * crashes from reaching this handler.
     */
    private setupErrorCapture(): void {
        if (!isBrowser || this.errorCapture) {
            return;
        }

        this.errorCapture = new ErrorCapture({
            send: (report) => {
                this.api.sendError(report).catch(() => {}); // Non-blocking
            },
            getContext: () => ({
                sessionId: this.sessionId,
                endUserId: this.endUserId,
                url: isBrowser ? sanitizeUrl(window.location.href) : '',
                release: this.release,
                environment: this.environment,
                commitSha: this.commitSha,
                dist: this.dist,
                sessionStartTimestampMs: this._sessionStartTimestamp ?? undefined,
                requestContext: Date.now() - this.lastRequestContextAt <= this.ERROR_REQUEST_WINDOW_MS
                    ? this.lastRequestContext
                    : undefined,
                automaticProperties: this.propertyManager.getAutomaticProperties(),
                userProperties: this.propertyManager.getUserProperties(),
                sessionProperties: this.propertyManager.getSessionProperties()
            }),
            breadcrumbs: this.breadcrumbs,
            filters: this.errorFilterOptions,
            captureThirdPartyResourceErrors: this.captureThirdPartyResourceErrorsFlag
        });

        this.errorCapture.install();
        logDebug('Error capture enabled');
    }

    /**
     * Record a breadcrumb (recent user action) for crash-report context.
     * Safe to call even when error tracking is disabled — the buffer just
     * accumulates and is never read.
     */
    private addBreadcrumb(type: BreadcrumbType, message: string, data?: Record<string, unknown>): void {
        try {
            this.breadcrumbs.add({ type, message, timestampMs: Date.now(), data });
        } catch {
            // Breadcrumbs are best-effort context; never break tracking.
        }
    }

    /**
     * Manually report a caught error from a try/catch path. The report is
     * marked `handled: true` (it didn't crash the app) and otherwise flows
     * through the same pipeline (breadcrumbs, dedup, send) as uncaught errors.
     * `options.componentStack` attaches a React component stack; `options.mechanism`
     * lets the React error boundary mark its reports as `react`.
     * No-op if error capture isn't active yet (before `start()` or when the
     * `enableErrorTracking` option is false). Never throws into the host app.
     */
    public captureException(
        error: unknown,
        options?: { componentStack?: string; mechanism?: ErrorMechanism },
    ): void {
        try {
            this.errorCapture?.capture(
                error,
                options?.mechanism ?? 'captureException',
                true,
                { componentStack: options?.componentStack },
            );
        } catch {
            // Manual capture must never break the host application.
        }
    }

    /**
     * Flush pending custom events (queued before 5 seconds)
     */
    private async flushPendingCustomEvents(): Promise<void> {
        if (this.pendingCustomEvents.length === 0) {
            return;
        }

        const eventsToFlush = [...this.pendingCustomEvents];
        this.pendingCustomEvents = [];

        logDebug(`Flushing ${eventsToFlush.length} pending custom events`);

        // Route the bridge from "queued behind the 5s gate" through the same
        // batching pipeline so we don't fire N individual requests on
        // session start.
        for (const { eventName, properties, eventId } of eventsToFlush) {
            this.queueCustomEvent(eventName, properties, eventId);
        }
    }

    /**
     * Flush pending logs (queued before 5 seconds)
     */
    private async flushPendingLogs(): Promise<void> {
        if (this.pendingLogs.length === 0) {
            return;
        }

        const logsToFlush = [...this.pendingLogs];
        this.pendingLogs = [];

        logDebug(`Flushing ${logsToFlush.length} pending logs`);

        for (const { logData } of logsToFlush) {
            try {
                await this.api.sendLog(logData);
            } catch (error) {
                logError('Failed to flush pending log:', error);
            }
        }
    }

    /**
     * Flush pending network errors (queued before 5 seconds)
     */
    private async flushPendingNetworkErrors(): Promise<void> {
        if (this.pendingNetworkErrors.length === 0) {
            return;
        }

        const errorsToFlush = [...this.pendingNetworkErrors];
        this.pendingNetworkErrors = [];

        logDebug(`Flushing ${errorsToFlush.length} pending network errors`);

        for (const { errorData } of errorsToFlush) {
            try {
                await this.api.sendNetworkError(errorData);
            } catch (error) {
                logError('Failed to flush pending network error:', error);
            }
        }
    }

    /**
     * Enable page load tracking - detects heavy page loads (>3 seconds)
     */
    public enablePageLoadTracking(): void {
        if (!isBrowser || typeof window === 'undefined') return;
        
        // Track initial page load
        if (document.readyState === 'complete') {
            // Page already loaded, check immediately
            this.trackPageLoad();
        } else {
            // Wait for page load
            window.addEventListener('load', () => {
                this.trackPageLoad();
            });
        }
        
        logDebug('Page load tracking enabled');
    }

    /**
     * Track heavy page loads using Performance API
     */
    private trackPageLoad(): void {
        if (!isBrowser || typeof performance === 'undefined') return;
        
        try {
            const perfEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
            if (!perfEntry) return;
            
            const loadDuration = perfEntry.loadEventEnd - perfEntry.fetchStart;
            const HEAVY_LOAD_THRESHOLD_MS = 3000; // 3 seconds
            
            // Only track if heavy (>3 seconds)
            if (loadDuration > HEAVY_LOAD_THRESHOLD_MS) {
                const requestId = uuidv1();
                const domContentLoaded = perfEntry.domContentLoadedEventEnd - perfEntry.fetchStart;
                const domComplete = perfEntry.domComplete - perfEntry.fetchStart;
                
                const errorData = {
                    requestId,
                    url: sanitizeUrl(window.location.href),
                    method: 'GET',
                    status: 200, // Page loads are typically successful
                    statusText: 'OK',
                    duration: loadDuration,
                    timestampMs: perfEntry.loadEventEnd + performance.timeOrigin,
                    sessionId: this.sessionId,
                    endUserId: this.endUserId,
                    errorType: 'slow_page_load',
                    errorMessage: `Page load took ${loadDuration}ms`,
                    // New span fields
                    startTimeMs: perfEntry.fetchStart + performance.timeOrigin,
                    spanName: 'page_load',
                    spanStatus: 'slow' as const,
                    attributes: {
                        'page.url': window.location.href,
                        'page.load_time': loadDuration,
                        'page.dom_content_loaded': domContentLoaded,
                        'page.dom_complete': domComplete,
                    }
                };
                // ✅ Check minimum duration - queue if below, send if above
                if (this.shouldSkipDueToMinimumDuration()) {
                    logDebug('Slow page load network error queued due to session duration below minimum');
                    this.pendingNetworkErrors.push({
                        errorData,
                        timestamp: Date.now()
                    });
                    return;
                }
                // ✅ Flush any pending network errors first (everything before 5 seconds)
                this.flushPendingNetworkErrors();
                this.api.sendNetworkError(errorData).catch(() => {}); // Non-blocking
            }
        } catch (error) {
            logWarn('Failed to track page load:', error);
        }
    }

    /**
     * Check if network request should be skipped (SDK's own requests)
     */
    private shouldSkipNetworkTracking(url: string): boolean {
        if (!url || !this.ingestionUrl) {
            return false;
        }
        
        try {
            const urlObj = new URL(url);
            const baseUrlObj = new URL(this.ingestionUrl);
            
            // Skip if same origin (same protocol, host, port)
            if (urlObj.origin === baseUrlObj.origin) {
                // Also check if it's an ingestion endpoint
                if (urlObj.pathname.startsWith('/api/ingestion/')) {
                    return true;
                }
            }
            
            // Also check string matching as fallback
            if (url.includes(this.ingestionUrl)) {
                return true;
            }
            
            return false;
        } catch (error) {
            // If URL parsing fails, do simple string check
            return url.includes(this.ingestionUrl);
        }
    }

    /**
     * Classify HTTP error status codes
     */
    private classifyHttpError(status: number): string {
        if (status >= 400 && status < 500) {
            return 'client_error';
        }
        if (status >= 500) {
            return 'server_error';
        }
        return 'unknown_error';
    }

    /**
     * Classify network errors (CORS, timeouts, blocked requests, etc.)
     */
    private classifyNetworkError(error: any): string {
        const errorMessage = error.message || '';
        const errorName = error.name || '';
        
        // Check for blocked requests (ad blockers, browser extensions, etc.)
        if (
            errorMessage.includes('blocked') ||
            errorMessage.includes('ERR_BLOCKED_BY_CLIENT') ||
            errorMessage.includes('net::ERR_BLOCKED_BY_CLIENT') ||
            errorName === 'TypeError' && errorMessage.includes('Failed to fetch')
        ) {
            return 'blocked_by_client';
        }
        
        // Check for CORS errors
        if (
            errorMessage.includes('CORS') ||
            errorMessage.includes('Cross-Origin') ||
            errorMessage.includes('Access-Control-Allow-Origin') ||
            errorName === 'TypeError' && errorMessage.includes('CORS')
        ) {
            return 'cors_error';
        }
        
        // Check for network/timeout errors
        if (
            errorMessage.includes('timeout') ||
            errorMessage.includes('TIMEOUT') ||
            errorMessage.includes('NetworkError') ||
            errorName === 'NetworkError'
        ) {
            return 'network_error';
        }
        
        // Check for abort errors
        if (
            errorMessage.includes('abort') ||
            errorName === 'AbortError'
        ) {
            return 'aborted';
        }
        
        return 'unknown_error';
    }

    /**
     * Disable console event tracking
     */
    public disableConsoleTracking(): void {
        if (!isBrowser || !this.consoleTrackingEnabled) return;

        // Restore original console methods
        if (this.originalConsole) {
            console.log = this.originalConsole.log;
            console.warn = this.originalConsole.warn;
            console.error = this.originalConsole.error;
        }

        this.consoleTrackingEnabled = false;
        logDebug('Console tracking disabled');
    }

    private trackConsoleEvent(level: 'log' | 'warn' | 'error', args: any[]): void {
        if (!this.initialized) {
            return;
        }

        // Only track warn and error, skip log
        if (level === 'log') {
            // Just call original console.log, don't track
            if (this.originalConsole) {
                this.originalConsole.log(...args);
            }
            return;
        }

        try {
            // ✅ SKIP TRACKING: If SDK logger is currently active, don't track
            if (isSDKLogging()) {
                if (this.originalConsole) {
                    this.originalConsole[level](...args);
                }
                return;
            }

            // ✅ SKIP TRACKING: Check if log originates from SDK code
            const stack = new Error().stack || '';
            if (this.isSDKStackFrame(stack)) {
                // This log came from SDK code, don't track it
                if (this.originalConsole) {
                    this.originalConsole[level](...args);
                }
                return;
            }

            const consoleData = {
                // Idempotency ID: the retry queue re-sends the same serialized
                // body, so the server can drop duplicate deliveries.
                eventId: uuidv1(),
                level: level, // 'warn' or 'error'
                message: args.map(arg =>
                    typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
                ).join(' '),
                timestampMs: Date.now(),
                url: isBrowser ? sanitizeUrl(window.location.href) : '',
                userAgent: isBrowser ? navigator.userAgent : '',
                stack: stack,
                environment: this.environment,
                sessionId: this.sessionId,
                endUserId: this.endUserId,
                automaticProperties: this.propertyManager.getAutomaticProperties()
            };

            // ✅ Check minimum duration - queue if below, send if above
            if (this.shouldSkipDueToMinimumDuration()) {
                logDebug(`Console ${level} queued due to session duration below minimum`);
                this.pendingLogs.push({
                    logData: consoleData,
                    timestamp: Date.now()
                });
                return;
            }

            // ✅ Flush any pending logs first (everything before 5 seconds)
            this.flushPendingLogs();

            // Record a console breadcrumb for crash-report context.
            this.addBreadcrumb('console', `${consoleData.level}: ${consoleData.message}`.substring(0, 200), { level: consoleData.level });

            // Promote React/Next hydration-mismatch console errors into structured
            // error reports — they surface as console.error and never reach
            // window.onerror. Marked handled (React recovers via client render).
            if (consoleData.level === 'error' && isHydrationError(consoleData.message)) {
                this.captureException(new Error(consoleData.message), { mechanism: 'react' });
            }

            // Send to dedicated endpoint for ClickHouse
            this.api.sendLog(consoleData).catch(err => {
                // Fallback to event stream if dedicated endpoint fails
            this.addEvent({
                type: 5, // Custom event type
                data: {
                    payload: {
                        eventType: 'console',
                        ...consoleData
                    }
                },
                timestamp: Date.now()
                }).catch(() => {}); // Silent fail
            });
        } catch (error) {
            logError('Error in trackConsoleEvent:', error);
        }
    }

    /**
     * Check if the actual caller (not SDK wrapper) is from SDK code
     * Since we intercept console methods, the stack will always include SDK frames.
     * We need to look at the actual caller frame (the one that called console.error/warn from user code).
     */
    private isSDKStackFrame(stack: string): boolean {
        if (!stack) return false;
        
        // SDK file path patterns to check for
        const sdkPatterns = [
            'humanbehavior-js',
            '@humanbehavior/core',
            '@humanbehavior/browser',
            'tracker.ts',
            'api.ts',
            'logger.ts',
            'utils/logger',
            'packages/core',
            'packages/browser',
            'index.mjs', // Built SDK bundle
            'index.js'   // Built SDK bundle
        ];
        
        // Parse stack into lines
        const stackLines = stack.split('\n');
        const stackLower = stack.toLowerCase();
        
        // If the ENTIRE stack only contains SDK patterns, it's an SDK-originated log
        // Otherwise, if there's ANY non-SDK frame, it's user code
        
        // Check if ALL frames (excluding the first "Error" line) are SDK frames
        let foundNonSDKFrame = false;
        
        for (let i = 0; i < stackLines.length; i++) {
            const line = stackLines[i].trim().toLowerCase();
            
            // Skip empty lines and the first "Error:" line
            if (!line || line === 'error' || line.startsWith('error:')) {
                continue;
            }
            
            // Check if this line contains SDK patterns
            const isSDKFrame = sdkPatterns.some(pattern => line.includes(pattern.toLowerCase()));
            
            if (!isSDKFrame) {
                // Found a non-SDK frame - this is user code calling console.error/warn
                foundNonSDKFrame = true;
                break;
            }
        }
        
        // If we found a non-SDK frame, it's user code - don't skip
        // If all frames are SDK frames, skip tracking
        return !foundNonSDKFrame;
    }

    /** Tracks whether unload listeners are already attached on the global
     *  window so we don't stack them across HMR / re-init cycles. The flag
     *  lives on `window`, not on `this`, because in dev environments
     *  (Next.js Turbopack HMR, module duplication) the instance can change
     *  even when window-level listeners are already in place. Stacking
     *  listeners means N copies of `pagehide` → N parallel sendBeacons →
     *  browsers throttle some, and only the first sub-100ms one survives
     *  reliably. The downstream symptom is a /session-end beacon that
     *  silently fails to deliver, making the dashboard fall through to the
     *  75-second age-out path instead of immediate eviction.
     */
    private setupPageUnloadHandler() {
        if (!isBrowser) return;
        const flag = '__humanBehaviorUnloadAttached';
        if ((window as any)[flag]) {
            logDebug('Page unload handlers already attached on window — skipping re-attach');
            // Update the bound tracker so this instance receives the calls
            // (the listener closes over `this` via a stable reference).
            (window as any).__humanBehaviorActiveTracker = this;
            return;
        }
        (window as any)[flag] = true;
        (window as any).__humanBehaviorActiveTracker = this;
        
        logDebug('Setting up page unload handler');
        
        // Handle visibility changes for sending events
        window.addEventListener('visibilitychange', () => {
            // Industry-standard active-time gating: every other replay tool
            // (Mixpanel, FullStory, Hotjar, MS Clarity) excludes hidden-tab
            // time from active duration. We capture the transition as an
            // rrweb custom event so the archiver and player segment logic
            // can clip activity at the boundary.
            //
            // Push BEFORE flushing on hidden so the marker ships in the
            // same batch as the events that immediately preceded it —
            // ensures the archiver sees the close before any trailing
            // mutations arrive in a later chunk.
            const state =
                document.visibilityState === 'hidden' ? 'hidden' : 'visible';
            this.emitVisibilityMarker(state);

            if (document.visibilityState === 'hidden') {
                logDebug('Page hidden - sending pending events');
                // Hidden is the last reliable signal on mobile (pagehide may
                // never fire when the app is killed): ship the gated analytics
                // buffer alongside the rrweb flush.
                this.drainPendingCustomEventsForTeardown();
                this.flushCustomEventBatchBeacon();
                this.flushEvents();
            } else if (document.visibilityState === 'visible') {
                logDebug('Page visible - taking full snapshot for multi-window replay');
                this.takeFullSnapshot();
            }
        });

        // Window focus: visible tabs on a secondary monitor still get rrweb noise
        // while blurred; gated with debounced blur so brief alt-tab does not jitter.
        this.setupWindowFocusTracking();

        // Use pagehide if available (more reliable than beforeunload)
        // pagehide fires in more cases (navigation, tab close, etc.)
        const unloadEvent = 'onpagehide' in window ? 'pagehide' : 'beforeunload';
        
        window.addEventListener(unloadEvent, () => {
            // ✅ SYNCHRONOUS UNLOAD HANDLER
            // Prepare data synchronously (no await) and send via sendBeacon
            logDebug('Page unloading - sending final events via sendBeacon');

            let sessionEndSent = false;
            const sendSessionEnd = () => {
                if (sessionEndSent) return;
                sessionEndSent = true;
                try {
                    this.api?.sendSessionEndBeacon(this.sessionId, this.endUserId);
                } catch {
                    // best-effort
                }
            };

            // Flush any in-memory customEvent batch BEFORE the minimum-
            // duration gate below — pagehide is the last chance to ship
            // these. The 100ms debounce in queueCustomEvent would otherwise
            // drop them on a fast tab close. `sendCustomEventBatch` uses
            // `keepalive: true` so the browser commits the in-flight POST
            // even after the page goes away. Fire-and-forget; we don't
            // await because that would block unload.
            try {
                this.drainPendingCustomEventsForTeardown();
                this.flushCustomEventBatchBeacon();
            } catch {
                // best-effort
            }

            // Flush buffered tracing spans on unload (sendBeacon, synchronous).
            try {
                this.tracing?.flush(true);
            } catch {
                // best-effort
            }

            // ✅ Check minimum duration before sending on unload (default: 5 seconds)
            const minimumDuration = this.minimumDurationMilliseconds;
            const sessionDuration = this.getSessionDuration();
            const isPositiveSessionDuration = sessionDuration !== null && sessionDuration >= 0;
            const isBelowMinimumDuration = 
                isPositiveSessionDuration && 
                sessionDuration < minimumDuration;
            
            if (isBelowMinimumDuration) {
                // Don't send - buffer stays
                logDebug(`Session duration (${sessionDuration}ms) below minimum (${minimumDuration}ms), not sending on unload`);
                sendSessionEnd();
                return;
            }
            
            // 1. Prepare events synchronously (copy queue immediately)
            const eventsToSend = [...this.eventQueue];
            
            // 2. Include pending snapshots if available (for very short sessions)
            // This handles cases where a flush is in progress but hasn't completed
            if (isBrowser && (window as any).__hb_pending_snapshots) {
                const pendingSnapshots = (window as any).__hb_pending_snapshots;
                if (Array.isArray(pendingSnapshots) && pendingSnapshots.length > 0) {
                    logDebug('Including pending FullSnapshot(s) in sendBeacon for short session');
                    eventsToSend.unshift(...pendingSnapshots); // Add at beginning so snapshot comes first
                    delete (window as any).__hb_pending_snapshots;
                }
            }
            
            // 3. Send via sendBeacon synchronously
            if (eventsToSend.length > 0 && this.api) {
                try {
                    // Get automatic properties synchronously
                    const automaticProperties = this.propertyManager.getAutomaticProperties();
                    
                    // Send via sendBeacon (synchronous API - completes before page closes)
                    this.api.sendBeaconEvents(
                        eventsToSend, 
                        this.sessionId, 
                        this.endUserId || undefined,
                        this.windowId,
                        automaticProperties
                    );
                    
                    // Clear queue after sending
                    this.eventQueue = [];
                } catch (error) {
                    // sendBeacon is best-effort, log but don't throw
                    logWarn('Failed to send events via sendBeacon on unload:', error);
                }
            }
            
            // 4. Also handle retry queue (for any failed requests)
            if (this.api) {
                this.api.unload();
            }

            // Evict from the dashboard's live-presence set LAST. Final event
            // beacons also refresh presence server-side; sending session-end
            // first lets those trailing writes re-add the user for the full
            // live-window timeout after a tab close.
            sendSessionEnd();
        });

        // Update activity timestamp on user interaction (not on page load)
        const updateActivity = () => {
            localStorage.setItem(`human_behavior_last_activity`, Date.now().toString());
        };

        // Listen for user interactions to update activity timestamp
        window.addEventListener('click', updateActivity);
        window.addEventListener('keydown', updateActivity);
        window.addEventListener('scroll', updateActivity);
        window.addEventListener('mousemove', updateActivity);
    }

    public viewLogs() {
        try {
            const logs = logger.getLogs();
            logInfo('HumanBehavior Logs:', logs);
            logger.clearLogs(); // Clear logs after viewing
        } catch (e) {
            logError('Failed to read logs:', e);
        }
    }

    /**
     * Add user identification information to the tracker
     * If userId is not provided, will use userProperties.email as the userId (if present)
     */
    public async identifyUser(
        userPropertiesOrArg: Record<string, any> | { userProperties: Record<string, any>; identityToken?: string },
        options?: { identityToken?: string }
    ): Promise<string> {
        // Accept either shape — the React wrapper passes
        // `{ userProperties }`, but tests/users routinely pass the bare
        // properties object. Both worked pre-0.7 only because the call sites
        // happened to spread compatibly; this normalises it.
        const wrapped = (userPropertiesOrArg as any)?.userProperties && typeof (userPropertiesOrArg as any).userProperties === 'object';
        const userProperties: Record<string, any> = wrapped
            ? (userPropertiesOrArg as any).userProperties
            : (userPropertiesOrArg as any) || {};

        // A token minted by the customer's backend, forwarded verbatim. Projects
        // with identity verification required reject an identify that claims an
        // externalUserId without one.
        const identityToken: string | undefined =
            options?.identityToken
            ?? (wrapped ? (userPropertiesOrArg as any).identityToken : undefined);

        // ✅ NON-BLOCKING: Don't wait for init, endUserId is already available locally
        // await this.ensureInitialized(); // Removed - no longer needed
        
        // Keep the original endUserId (UUID) - don't change it
        const originalEndUserId = this.endUserId;
        
        // Store user properties on BOTH the local cache and the
        // PropertyManager so getUserAttributes() and getAllProperties().user
        // see the same set. Pre-0.7 only the local field was set, which made
        // identify() effectively write-only.
        this.userProperties = { ...this.userProperties, ...userProperties };
        try {
            this.propertyManager?.setUserProperties(userProperties);
        } catch {
            // PropertyManager not yet constructed — ignore (constructor sets
            // it before init() runs in practice).
        }

        logDebug('Identifying user:', { userProperties, originalEndUserId, sessionId: this.sessionId });
        
        // Get automatic properties and send with user data (only in browser)
        const automaticProperties = isBrowser ? this.propertyManager.getAutomaticProperties() : {};
        
        // Use the API class method which properly handles user name
        const userResponse = await this.api.sendUserData(
            originalEndUserId || '',
            userProperties,
            this.sessionId,
            identityToken
        );

        // If server found a preexisting user, persist the canonical ID for future sessions
        // but keep using the original anon ID for the current session
        if (userResponse.actualUserId || userResponse.wasExistingUser) {
            const canonicalEndUserId = userResponse.actualUserId || originalEndUserId;
            if (canonicalEndUserId && canonicalEndUserId !== originalEndUserId) {
                // Persist canonical ID to cookie/localStorage for future sessions
                const cookieName = `human_behavior_end_user_id`;
                this.setCookie(cookieName, canonicalEndUserId, 365);
                // Explicitly set localStorage as well to ensure it's persisted
                if (isBrowser) {
                    try {
                        localStorage.setItem(cookieName, canonicalEndUserId);
                    } catch (error) {
                        logDebug('Failed to set canonical endUserId in localStorage:', error);
                    }
                }
                logDebug(`🔗 Preexisting user detected. Future sessions will use canonical ID: ${canonicalEndUserId} (current session stays: ${originalEndUserId})`);
            }
        }

        // Keep original endUserId for the entire session (no mid-session switch)
        return originalEndUserId || '';
    }
    /**
     * Get current user attributes
     */
    public getUserAttributes(): Record<string, any> {
        // Combine the local cache with whatever the PropertyManager has
        // recorded. setUserProperty() (the public API) only writes to the
        // PropertyManager; identifyUser() writes to both.
        const fromManager = (() => {
            try {
                return this.propertyManager?.getAllProperties().user || {};
            } catch {
                return {};
            }
        })();
        return { ...fromManager, ...this.userProperties };
    }

    public async start() {
        // ✅ NON-BLOCKING: Start immediately, don't wait for init
        // Init continues in background but doesn't block recording
        if (!isBrowser) return;
        
        // Prevent multiple start() calls
        if (this.isStarted) {
            logDebug('HumanBehaviorTracker already started, skipping start() call.');
            return;
        }
        this.isStarted = true;
        
        // ✅ Initialize idle detection
        // Sync with session activity timestamp if it exists (from session creation)
        // Otherwise use current time
        this._lastActivityTimestamp = this._sessionActivityTimestamp !== null 
            ? this._sessionActivityTimestamp 
            : Date.now();
        this._isIdle = 'unknown'; // Start as unknown until first interaction

        // Start periodic flushing (unified queue) at the current tier.
        this.armFlushTimer();

        // Periodic presence ping for the dashboard's live indicator. Skips
        // when the tab is hidden (browsers throttle setInterval there
        // anyway) and when the SDK hasn't finished bootstrapping yet
        // (sessionId not assigned). Best-effort — failures are silent.
        this.heartbeatInterval = window.setInterval(() => {
            try {
                if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
                    return;
                }
                if (!this.sessionId || !this.api) return;
                this.api.sendHeartbeatBeacon(this.sessionId, this.endUserId);
            } catch {
                // best-effort
            }
        }, this.HEARTBEAT_INTERVAL_MS);

        // ✅ Enable console tracking for warn/error logging (if enabled)
        if (this.enableConsoleTrackingFlag) {
            this.enableConsoleTracking();
        }
        
        // ✅ Enable network error tracking (if enabled)
        if (this.enableNetworkTrackingFlag) {
            this.enableNetworkTracking();
        }

        // ✅ Enable Core Web Vitals tracking (if enabled)
        if (this.enableWebVitalsFlag) {
            this.enableWebVitalsTracking();
        }

        // ✅ Enable distributed tracing (if enabled)
        if (this.enableTracingFlag) {
            this.enableTracing();
        }

        // ✅ Enable crash/error capture (if enabled)
        if (this.enableErrorTrackingFlag) {
            this.setupErrorCapture();
        }
        
        // Enable page load tracking
        this.enablePageLoadTracking();

        // Trigger server-side GeoIP enrichment once per session (fire-and-forget).
        // Server resolves IP from request headers and publishes a $geoip event.
        this.api.sendIpInfo(this.sessionId, this.endUserId);

        // ✅ DOM READY DETECTION
        // Wait for DOM to be ready before starting recording
        const startRecording = () => {
            // Prevent multiple recording instances
            if (this.recordInstance) {
                logDebug('🎯 Recording already started, skipping duplicate start');
                return;
            }
            
            logDebug('🎯 DOM ready, starting session recording');
            
            // ✅ HUMANBEHAVIOR RRWEB CONFIGURATION
            this.rrwebRecord = record;
            // debug removed
            const recordInstance = record({
            emit: (event) => {
                this.addRecordingEvent(event);
                
                // ✅ DEBUG FULLSNAPSHOT GENERATION
                if (event.type === 2) { // FullSnapshot
                    logDebug(`🎯 FullSnapshot generated at ${new Date().toISOString()}`);
                }
            },
            // ✅ HUMANBEHAVIOR'S CUSTOM SETTINGS
            maskTextSelector: this.redactionManager.getMaskTextSelector() || undefined,
            maskTextFn: undefined,
            maskAllInputs: this.redactionManager.getRedactionMode() === 'privacy-first', // Configurable based on strategy
            maskInputOptions: {
                // Enable rrweb input masking callbacks for all common types
                password: true,
                text: true,
                textarea: true,
                email: true,
                number: true,
                tel: true,
                url: true,
                search: true,
                date: true,
                time: true,
                month: true,
                week: true
            },
            // In visibility-first, selectively mask inputs that should be redacted
            maskInputFn: (text, element) => {
                const masked = '*'.repeat(text.length || 1);
                try {
                    const mode = this.redactionManager.getRedactionMode();
                    // If we can't positively identify the element, mask it.
                    if (!(element instanceof HTMLElement)) return masked;
                    // privacy-first: always mask input values
                    if (mode === 'privacy-first') return masked;
                    // visibility-first: show only elements the manager says are safe.
                    return this.redactionManager.shouldUnredactElement(element) ? text : masked;
                } catch {
                    // Fail closed: a thrown redaction decision must never leak the
                    // raw input value into the recording.
                    return masked;
                }
            },
            slimDOMOptions: {},
            // ✅ ERROR SUPPRESSION SETTINGS - Disabled to prevent console noise
            collectFonts: false, // Disable font collection to reduce errors
            inlineStylesheet: true, // Keep styles for proper session replay
            recordCrossOriginIframes: false, // Prevent cross-origin iframe errors
            
            // ✅ CANVAS RECORDING - protection against overwhelm
            recordCanvas: this.recordCanvas, // Opt-in only
            sampling: this.recordCanvas ? { canvas: 4 } : undefined, // 4 FPS throttle
            dataURLOptions: this.recordCanvas ? { 
                type: 'image/webp', 
                quality: 0.4 
            } : undefined, // WebP with 40% quality
            
            // ✅ FULLSNAPSHOT GENERATION - No periodic snapshots to avoid animation issues
            // Rely on initial FullSnapshot + navigation-triggered ones only
            hooks: {
                // Extra safety: mask input events selectively using rrweb hook
                input: (event) => {
                    try {
                        const mode = this.redactionManager.getRedactionMode();
                        // In privacy-first everything is masked already by maskAllInputs
                        if (mode === 'privacy-first') return;
                        const node = typeof document !== 'undefined'
                          ? document.querySelector(`[data-rrweb-id="${(event as any).id}"]`)
                          : null;
                        if (node && node instanceof HTMLElement) {
                            const shouldShow = this.redactionManager.shouldUnredactElement(node);
                            if (!shouldShow) {
                                // Mask text payloads in input event
                                if (typeof (event as any).text !== 'undefined') {
                                    (event as any).text = '*'.repeat((event as any).text?.length || 1);
                                }
                                if (typeof (event as any).value !== 'undefined') {
                                    (event as any).value = '*'.repeat((event as any).value?.length || 1);
                                }
                            }
                        }
                    } catch {}
                }
            }
        });
        
        // Store the record instance for cleanup 
        this.recordInstance = recordInstance || null;

        // Replay-fidelity: capture the text of stylesheets rrweb could not
        // inline (CORS-opaque cross-origin sheets) and ship it in-band as
        // custom events, so replay renders the record-time CSS instead of
        // whatever the origin serves later. Never throws, fully async.
        try {
            this.cssSnapshotCapture?.dispose();
            this.cssSnapshotCapture = new CssSnapshotCapture((payload) => {
                try {
                    if (this.rrwebRecord && typeof this.rrwebRecord.addCustomEvent === 'function') {
                        this.rrwebRecord.addCustomEvent(CSS_SNAPSHOT_TAG, payload);
                    }
                } catch {
                    // Recording may have stopped between fetch and emit.
                }
            });
            this.cssSnapshotCapture.captureFromDocument(document);
        } catch {
            // CSS snapshotting must never break recording.
        }

        // Localhost Verify fidelity: ship same-origin image bytes in-band so
        // the cloud live preview can render them (archiver cannot fetch loopback).
        // Push onto eventQueue directly (same as $visibility markers) — large
        // base64 payloads are more reliable here than rrweb addCustomEvent.
        // No-op on public origins. Never throws.
        try {
            this.localAssetSnapshotCapture?.dispose();
            this.localAssetSnapshotCapture = new LocalAssetSnapshotCapture((payload) => {
                try {
                    this.eventQueue.push({
                        type: 5,
                        data: { tag: LOCAL_ASSET_TAG, payload },
                        timestamp: Date.now(),
                    } as any);
                    // Nudge flush so Verify sees the image soon after paint.
                    void this.flushEvents();
                } catch {
                    // Recording may have stopped between fetch and emit.
                }
            });
            if (typeof location !== 'undefined') {
                this.localAssetSnapshotCapture.captureFromDocument(document, location.href);
            }
        } catch {
            // Local asset snapshotting must never break recording.
        }

        // Invisible-text capture: read getComputedStyle colors for text elements
        // and ship an `hb-contrast` custom event for any whose foreground matches
        // its background (white-on-white validation errors, invisible prices).
        // The deterministic trace compiler cannot see class/CSS-in-JS colors, so
        // this is the only path that catches them. Never throws.
        try {
            this.contrastCapture?.dispose();
            this.contrastCapture = new ContrastCapture(
                (payload) => {
                    try {
                        if (this.rrwebRecord && typeof this.rrwebRecord.addCustomEvent === 'function') {
                            this.rrwebRecord.addCustomEvent(HB_CONTRAST_TAG, payload);
                        }
                    } catch {
                        // Recording may have stopped between scan and emit.
                    }
                },
                (node) => {
                    try {
                        return this.rrwebRecord?.mirror?.getId(node) ?? -1;
                    } catch {
                        return -1;
                    }
                },
                (payload) => {
                    try {
                        if (this.rrwebRecord && typeof this.rrwebRecord.addCustomEvent === 'function') {
                            this.rrwebRecord.addCustomEvent(HB_CLIP_TAG, payload);
                        }
                    } catch {
                        // Recording may have stopped between scan and emit.
                    }
                },
                (payload) => {
                    try {
                        if (this.rrwebRecord && typeof this.rrwebRecord.addCustomEvent === 'function') {
                            this.rrwebRecord.addCustomEvent(HB_OVERLAP_TAG, payload);
                        }
                    } catch {
                        // Recording may have stopped between scan and emit.
                    }
                },
                (payload) => {
                    try {
                        if (this.rrwebRecord && typeof this.rrwebRecord.addCustomEvent === 'function') {
                            this.rrwebRecord.addCustomEvent(HB_MISALIGN_TAG, payload);
                        }
                    } catch {
                        // Recording may have stopped between scan and emit.
                    }
                },
                (payload) => {
                    try {
                        if (this.rrwebRecord && typeof this.rrwebRecord.addCustomEvent === 'function') {
                            this.rrwebRecord.addCustomEvent(HB_BROKEN_ASSET_TAG, payload);
                        }
                    } catch {
                        // Recording may have stopped between scan and emit.
                    }
                },
            );
            if (isBrowser) this.contrastCapture.start(document, window);
        } catch {
            // Contrast capture must never break recording.
        }

        // Broken-asset capture: a failed <img>/<script>/<link>/media load fires a
        // capture-phase `error` on the element (it does not bubble). Ship it as an
        // `hb-broken-asset` custom event with the element's rrweb node id so the
        // deterministic trace compiler can emit a broken_asset row and prime the
        // visual grader. The error subsystem separately records the runtime issue;
        // this marker is the replay-stream + element-localized signal. Never throws.
        try {
            if (isBrowser) {
                if (this.brokenAssetHandler) {
                    window.removeEventListener('error', this.brokenAssetHandler, true);
                }
                this.brokenAssetSeen.clear();
                const ASSET_TAGS = new Set(['IMG', 'SCRIPT', 'LINK', 'SOURCE', 'VIDEO', 'AUDIO', 'IFRAME']);
                this.brokenAssetHandler = (event: Event) => {
                    try {
                        const target = event.target as (Element & { src?: string; href?: string }) | null;
                        if (!target || !target.tagName || !ASSET_TAGS.has(target.tagName)) return;
                        const url = target.src || target.href || '';
                        if (!url || this.brokenAssetSeen.has(url)) return;
                        if (this.brokenAssetSeen.size >= 100) return;
                        this.brokenAssetSeen.add(url);
                        let id = -1;
                        try {
                            id = this.rrwebRecord?.mirror?.getId(target) ?? -1;
                        } catch {
                            id = -1;
                        }
                        if (this.rrwebRecord && typeof this.rrwebRecord.addCustomEvent === 'function') {
                            this.rrwebRecord.addCustomEvent(HB_BROKEN_ASSET_TAG, {
                                id,
                                tag: target.tagName.toLowerCase(),
                                url,
                            });
                        }
                    } catch {
                        // Marker emission is best-effort.
                    }
                };
                window.addEventListener('error', this.brokenAssetHandler, true);
            }
        } catch {
            // Broken-asset capture must never break recording.
        }
        };

        // ✅ DOM READY DETECTION - More aggressive like previous version
        logDebug(`🎯 DOM ready state: ${document.readyState}`);
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
            // DOM is ready enough, start immediately
            logDebug(`🎯 DOM ready (${document.readyState}), starting recording immediately`);
            startRecording();
        } else {
            // Wait for DOM to be ready, but also check periodically
            logDebug('🎯 DOM not ready, waiting for DOMContentLoaded event');
            
            const checkDomReady = () => {
                if (document.readyState === 'interactive' || document.readyState === 'complete') {
                    logDebug(`🎯 DOM ready (${document.readyState}), starting recording`);
                    startRecording();
                    return true;
                }
                return false;
            };
            
            // Check immediately in case it changed
            if (checkDomReady()) return;
            
            // Listen for DOMContentLoaded
            document.addEventListener('DOMContentLoaded', () => {
                logDebug('🎯 DOMContentLoaded fired, starting recording');
                startRecording();
            }, { once: true });
            
            // Also check periodically for faster response
            const interval = setInterval(() => {
                if (checkDomReady()) {
                    clearInterval(interval);
                }
            }, 10); // Check every 10ms
            
            // Clear interval after 5 seconds to avoid infinite checking
            setTimeout(() => clearInterval(interval), 5000);
        }
    }

    /**
     * Push an rrweb custom event (type 5) that records a tab visibility
     * transition. The archiver and replay player both consume this marker
     * to gate active-duration calculations: time when state==='hidden' is
     * excluded from "Active Time Spent" regardless of any rrweb activity
     * that happens during it (animations, ad rotations, etc).
     *
     * Best-effort: failures are silent. The marker is just a hint; the
     * existing 30s AFK + 2.5s gap rules still bound active time even if
     * we drop one of these.
     */
    private emitVisibilityMarker(state: 'hidden' | 'visible'): void {
        try {
            const event = {
                type: 5, // rrweb EventType.Custom
                data: { tag: '$visibility', payload: { state } },
                timestamp: Date.now(),
            };
            this.eventQueue.push(event as any);
        } catch {
            // best-effort
        }
    }

    /** rrweb Custom event for archiver/player: engaged = visible && focused. */
    private emitFocusMarker(state: 'focused' | 'blurred'): void {
        try {
            const event = {
                type: 5,
                data: { tag: '$focus', payload: { state } },
                timestamp: Date.now(),
            };
            this.eventQueue.push(event as any);
        } catch {
            // best-effort
        }
    }

    private setupWindowFocusTracking(): void {
        if (!isBrowser) return;
        try {
            if (typeof document.hasFocus === 'function' && !document.hasFocus()) {
                this.emitFocusMarker('blurred');
                this.lastEmittedFocusState = 'blurred';
            }
        } catch {
            // best-effort
        }

        window.addEventListener('blur', () => this.onWindowBlur(), false);
        window.addEventListener('focus', () => this.onWindowFocus(), false);
    }

    private onWindowBlur(): void {
        if (!isBrowser) return;
        if (this.focusBlurGraceTimeout !== null) {
            clearTimeout(this.focusBlurGraceTimeout);
        }
        this.focusBlurGraceTimeout = window.setTimeout(() => {
            this.focusBlurGraceTimeout = null;
            try {
                if (typeof document.hasFocus === 'function' && document.hasFocus()) return;
                if (this.lastEmittedFocusState === 'blurred') return;
                this.emitFocusMarker('blurred');
                this.lastEmittedFocusState = 'blurred';
            } catch {
                // noop
            }
        }, this.FOCUS_BLUR_GRACE_MS);
    }

    private onWindowFocus(): void {
        if (!isBrowser) return;
        if (this.focusBlurGraceTimeout !== null) {
            clearTimeout(this.focusBlurGraceTimeout);
            this.focusBlurGraceTimeout = null;
        }
        if (this.lastEmittedFocusState !== 'blurred') return;
        try {
            this.emitFocusMarker('focused');
            this.lastEmittedFocusState = 'focused';
        } catch {
            // noop
        }
    }

    /**
     * Manually trigger a FullSnapshot (for navigation events)
     * Delays snapshot to avoid capturing mid-animation states
     */
    private takeFullSnapshot(): void {
        // Clear any existing timeout to avoid multiple snapshots
        if (this.fullSnapshotTimeout) {
            clearTimeout(this.fullSnapshotTimeout);
        }

        // Delay FullSnapshot to let animations settle
        this.fullSnapshotTimeout = window.setTimeout(() => {
            // Wait for any pending animations/transitions to complete
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    try {
                        // Access takeFullSnapshot from the rrweb record function
                        if (this.rrwebRecord && typeof this.rrwebRecord.takeFullSnapshot === 'function') {
                            this.rrwebRecord.takeFullSnapshot();
                            logDebug('✅ FullSnapshot taken (delayed for animations)');
                            // A navigation may have introduced stylesheets the
                            // snapshot could not inline; capture their text too.
                            this.cssSnapshotCapture?.captureFromDocument(document);
                            if (typeof location !== 'undefined') {
                                this.localAssetSnapshotCapture?.captureFromDocument(
                                    document,
                                    location.href,
                                );
                            }
                        } else {
                            logWarn('⚠️ takeFullSnapshot not available on record function');
                        }
                    } catch (error) {
                        logError('❌ Failed to take FullSnapshot:', error);
                    }
                });
            });
        }, 1000); // Wait 1 second for animations to settle
    }

    public async stop() {
        await this.ensureInitialized();
        if (!isBrowser) return;
        
        if (this.flushInterval) {
            clearInterval(this.flushInterval);
            this.flushInterval = null;
        }

        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }

        // Stop rrweb recording
        if (this.recordInstance) {
            this.recordInstance();
            this.recordInstance = null;
        }
        
        // Clear any pending FullSnapshot timeouts
        if (this.fullSnapshotTimeout) {
            clearTimeout(this.fullSnapshotTimeout);
            this.fullSnapshotTimeout = null;
        }

        if (this.cssSnapshotCapture) {
            this.cssSnapshotCapture.dispose();
            this.cssSnapshotCapture = null;
        }

        if (this.localAssetSnapshotCapture) {
            this.localAssetSnapshotCapture.dispose();
            this.localAssetSnapshotCapture = null;
        }

        if (this.contrastCapture) {
            this.contrastCapture.dispose();
            this.contrastCapture = null;
        }

        if (this.brokenAssetHandler && typeof window !== 'undefined') {
            window.removeEventListener('error', this.brokenAssetHandler, true);
            this.brokenAssetHandler = null;
        }

        if (this.focusBlurGraceTimeout !== null) {
            clearTimeout(this.focusBlurGraceTimeout);
            this.focusBlurGraceTimeout = null;
        }

        this.rrwebRecord = null;

        // Disable console tracking
        this.disableConsoleTracking();

        // Cleanup navigation tracking
        this.cleanupNavigationTracking();

        // Cleanup rage/dead click tracking
        if (this.frictionMutationObserver) {
            this.frictionMutationObserver.disconnect();
            this.frictionMutationObserver = null;
        }
        this.frictionClicks?.reset();
    }

    /**
     * Add an event to the ingestion queue
     * Events are sent directly without processing to avoid corruption
     */
    public async addEvent(event: any) {
        // ✅ NON-BLOCKING: Events work immediately, no waiting for init
        // endUserId and sessionId are already available locally
        
        // ✅ CHECK SESSION TIMEOUT before adding event (creates new session if expired)
        if (isBrowser) {
            this.checkAndRefreshSession();
        }
        
        // ✅ DIRECT EVENT HANDLING - No custom processing to avoid corruption
        // Events flow directly from rrweb to ingestion server
        
        // ✅ EVENT VALIDATION
        if (!event || typeof event !== 'object') {
            logDebug('⚠️ Skipping invalid event:', event);
            return;
        }
        
        // ✅ LOG FULLSNAPSHOT STATUS FOR DEBUGGING
        if (event.type === 2) { // FullSnapshot
            const hasData = !!event.data;
            const hasNode = !!(event.data && event.data.node);
            
            if (!hasData || !hasNode) {
                logDebug(`⚠️ Empty FullSnapshot detected: hasData=${hasData}, hasNode=${hasNode} - continuing session`);
            } else {
                logDebug(`✅ Valid FullSnapshot: hasData=${hasData}, hasNode=${hasNode}, dataType=${event.data?.node?.type}`);
            }
        }
        
        // Queue size management with immediate flushing
        if (this.eventQueue.length >= this.MAX_QUEUE_SIZE) {
            // Drop oldest event when queue is full
            this.eventQueue.shift();
            logDebug('Queue is full, the oldest event is dropped.');
        }
        
        this.eventQueue.push(event); // Direct event handling
        
        // Immediate flush for FullSnapshots (important events)
        if (event.type === 2) { // FullSnapshot
            logDebug('FullSnapshot added, triggering immediate flush');
            this.flushEvents();
        }
        // Immediate flush if queue is getting large
        else if (this.eventQueue.length >= this.MAX_QUEUE_SIZE * 0.8) {
            logDebug(`Queue at ${this.eventQueue.length}/${this.MAX_QUEUE_SIZE}, triggering immediate flush`);
            this.flushEvents();
        }
    }

    /**
     * Calculate session duration
     * Uses most recent event timestamp minus session start timestamp
     */
    private getSessionDuration(): number | null {
        // Use session start timestamp if available, otherwise fall back to sessionStartTime
        const sessionStart = this._sessionStartTimestamp ?? this.sessionStartTime;
        if (!sessionStart) {
            return null;
        }
        
        // Get most recent event timestamp from queue
        const eventsWithTimestamps = this.eventQueue.filter((e: any) => e && e.timestamp);
        if (eventsWithTimestamps.length === 0) {
            return null;
        }
        
        const mostRecentEvent = eventsWithTimestamps.reduce((latest: any, current: any) => {
            return (!latest || (current.timestamp && current.timestamp > latest.timestamp)) ? current : latest;
        }, null);
        
        if (!mostRecentEvent || !mostRecentEvent.timestamp) {
            return null;
        }
        
        // Calculate duration (both timestamps should be in milliseconds)
        const duration = mostRecentEvent.timestamp - sessionStart;
        return duration >= 0 ? duration : null;
    }

    /**
     * Check if session duration is below minimum threshold
     * Returns true if we should skip sending (session too short)
     */
    private shouldSkipDueToMinimumDuration(): boolean {
        const minimumDuration = this.minimumDurationMilliseconds;
        const sessionDuration = this.getSessionDuration();
        const isPositiveSessionDuration = sessionDuration !== null && sessionDuration >= 0;
        const isBelowMinimumDuration =
            isPositiveSessionDuration &&
            sessionDuration < minimumDuration;

        if (isBelowMinimumDuration) {
            logDebug(`Session duration (${sessionDuration}ms) below minimum (${minimumDuration}ms), skipping send`);
            return true;
        }

        return false;
    }

    /**
     * Flush events to the ingestion server
     * Events are sent in chunks to handle large payloads efficiently
     */
    /**
     * (Re)start the periodic flush interval at the cadence for the current
     * tier. Idempotent: safe to call repeatedly; only the interval handle is
     * recreated.
     */
    private armFlushTimer(): void {
        if (!isBrowser) return;
        if (this.flushInterval) {
            clearInterval(this.flushInterval);
            this.flushInterval = null;
        }
        const intervalMs = this.flushTier === 'live'
            ? this.LIVE_FLUSH_INTERVAL_MS
            : this.IDLE_FLUSH_INTERVAL_MS;
        this.flushInterval = window.setInterval(() => {
            this.flushEvents();
            // Also drain anything a previous failed flush persisted to storage,
            // so durable events are delivered in-session rather than waiting for
            // the next page load. Re-entrant + no-op when the queue is empty.
            this.api.flushPersistedEvents();
        }, intervalMs);
    }

    /**
     * React to the server's `liveViewerActive` signal (read off the /events
     * response). A positive signal pushes the live-tier deadline forward and
     * switches to the fast cadence; once the grace window lapses with no fresh
     * positive signal, we fall back to the idle cadence. Only re-arms the timer
     * when the tier actually changes, so this is cheap to call every flush.
     */
    private noteLiveViewerSignal(active: boolean): void {
        const now = Date.now();
        if (active) {
            this.liveFlushDeadline = now + this.LIVE_FLUSH_GRACE_MS;
        }
        const nextTier: 'idle' | 'live' = now < this.liveFlushDeadline ? 'live' : 'idle';
        if (nextTier !== this.flushTier) {
            this.flushTier = nextTier;
            this.armFlushTimer();
        }
    }

    private async flushEvents() {
        // Prevent concurrent flushes
        // ✅ NON-BLOCKING: Don't check initialized - events work immediately
        if (this.isProcessing) {
            return;
        }

        // Don't make requests if monthly limit is reached - silently skip
        if (this.monthlyLimitReached) {
            return; // Silently skip without logging
        }
        
        // ✅ IDLE STATE: Don't flush when idle and queue is empty (events are being skipped anyway)
        // But allow flush if we have events queued (e.g., from before going idle or FullSnapshots)
        if (this._isIdle === true && this.eventQueue.length === 0) {
            return;
        }
        
        const hasFullSnapshotQueued = this.eventQueue.some(e => e && e.type === 2);

        // ✅ Check minimum duration before flushing (default: 5 seconds).
        // FullSnapshots must bypass this gate so live replay can render the
        // page immediately during install verification and first page load.
        const minimumDuration = this.minimumDurationMilliseconds;
        const sessionDuration = this.getSessionDuration();
        const isPositiveSessionDuration = sessionDuration !== null && sessionDuration >= 0;
        const isBelowMinimumDuration = 
            isPositiveSessionDuration && 
            sessionDuration < minimumDuration;
        
        if (isBelowMinimumDuration && !hasFullSnapshotQueued) {
            // Don't flush - schedule retry
            logDebug(`Session duration (${sessionDuration}ms) below minimum (${minimumDuration}ms), buffering`);
            // Schedule retry after buffer timeout (2 seconds)
            setTimeout(() => {
                this.flushEvents();
            }, 2000);
            return;
        }

        this.isProcessing = true;
        try {
            // ✅ CRITICAL FIX FOR SHORT SESSIONS:
            // Swap the current queue with an empty one atomically
            // BUT: Store a reference to FullSnapshots so they can be re-sent on unload if needed
            const eventsToProcess = this.eventQueue;
            const fullSnapshotsInFlush = eventsToProcess.filter(e => e && e.type === 2);
            this.eventQueue = [];
            
            // Store FullSnapshots that are being flushed so they can be sent via sendBeacon on unload
            // if the HTTP request doesn't complete in time (for very short sessions)
            if (fullSnapshotsInFlush.length > 0 && isBrowser) {
                // Store in a way that's accessible during unload
                (window as any).__hb_pending_snapshots = fullSnapshotsInFlush;
                // Clear after a delay (long enough for HTTP to complete, short enough to not waste memory)
                setTimeout(() => {
                    delete (window as any).__hb_pending_snapshots;
                }, 5000); // 5 seconds should be enough for HTTP to complete
            }

            if (eventsToProcess.length > 0) {
                logDebug('Flushing events:', eventsToProcess);
                
                // ✅ LOG FULLSNAPSHOT STATUS FOR MONITORING
                const fullSnapshots = eventsToProcess.filter(e => e.type === 2);
                if (fullSnapshots.length > 0) {
                    logDebug(`[FIXED] Sending ${fullSnapshots.length} FullSnapshot(s) with valid data`);
                }
                
                try {
                    // ✅ Include all IDs in payload (endUserId, sessionId, windowId)
                    // ✅ Include automatic properties for user creation on first event
                    // Server will create user/session on first event if needed
                    const automaticProperties = this.propertyManager.getAutomaticProperties();
                    const sendResults = await this.api.sendEventsChunked(
                        eventsToProcess, 
                        this.sessionId, 
                        this.endUserId!,
                        this.windowId,
                        automaticProperties
                    );
                    // If a dashboard live viewer is watching this project, the
                    // server flags it on the events response — speed up the
                    // flush cadence so the live replay stays near real-time.
                    const liveViewerActive = Array.isArray(sendResults)
                        && sendResults.some((r) => r && (r as any).liveViewerActive === true);
                    this.noteLiveViewerSignal(liveViewerActive);
                } catch (error: any) {
                    // sendEventsChunked persists the batch to durable storage
                    // before it throws, so these are queued for retry (drained
                    // in-session by flushPersistedEvents + on the next load), not
                    // lost — except a session that the server has already closed,
                    // which can no longer accept them.
                    const msg = String(error?.message ?? error ?? '');
                    if (msg.includes('ERROR: Session already completed')) {
                        logWarn('Session already completed on the server; dropping events for the expired session');
                    } else if (msg.includes('413') || msg.includes('Content Too Large')) {
                        logWarn('Payload too large; events persisted for retry');
                    } else if (
                        msg.includes('ERR_BLOCKED_BY_CLIENT') ||
                        msg.includes('Failed to fetch') ||
                        msg.includes('NetworkError') ||
                        msg.includes('Failed to send events') ||
                        msg.includes('Request timeout') ||
                        error?.name === 'TimeoutError' ||
                        error?.name === 'AbortError'
                    ) {
                        logWarn('Request blocked by ad blocker, timed out, or network issue; events persisted for retry');
                    } else {
                        throw error;
                    }
                }
            }

            // ✅ Once we hit 5 seconds, flush all pending events (custom events, logs, network errors)
            // This ensures everything that happened before 5 seconds gets sent
            // Flush AFTER eventQueue to prioritize session recording events
            await this.flushPendingCustomEvents();
            await this.flushPendingLogs();
            await this.flushPendingNetworkErrors();
        } finally {
            this.isProcessing = false;
        }
    }

    /**
     * Check if an event represents user interaction (not background DOM mutations)
     */
    private isInteractiveEvent(event: any): boolean {
        // Event type 3 = IncrementalSnapshot
        if (event.type !== 3) {
            return false;
        }
        
        // Active sources that indicate user interaction
        // Source values from rrweb: 0=DomContentLoaded, 1=MouseMove, 2=MouseInteraction, 3=Scroll, 
        // 4=ViewportResize, 5=Input, 6=MediaInteraction, 7=StyleSheetRule, 8=CanvasMutation, 
        // 9=Font, 10=Log, 11=Drag, 12=StyleDeclaration, 13=Selection, 14=AdoptedStyleSheet, 15=Mutation
        const ACTIVE_SOURCES = [1, 2, 3, 4, 5, 6, 11]; // MouseMove, MouseInteraction, Scroll, ViewportResize, Input, MediaInteraction, Drag
        
        const source = event.data?.source;
        return ACTIVE_SOURCES.includes(source);
    }
    
    /**
     * Update idle state based on event activity
     * Also updates session activity timestamp to prevent premature session expiration
     */
    private updateIdleState(event: any): void {
        const isUserInteraction = this.isInteractiveEvent(event);
        const currentTime = event.timestamp || Date.now();
        
        // Update activity timestamp on user interaction
        if (isUserInteraction) {
            const wasIdle = this._isIdle === true;
            this._lastActivityTimestamp = currentTime;
            
            // ✅ CRITICAL: Also update session activity timestamp to prevent session expiration
            // This ensures the SESSION_IDLE_TIMEOUT_MS session timeout is extended on user interaction
            // (checkAndRefreshSession will handle persistence, but we update memory here)
            if (this._sessionActivityTimestamp !== null) {
                this._sessionActivityTimestamp = currentTime;
            }
            
            // If we were idle and user interacts, exit idle state
            if (wasIdle) {
                logDebug('✅ User activity detected, exiting idle state');
                this._isIdle = false;
                
                // Take full snapshot when returning from idle to capture current state
                if (this.rrwebRecord && typeof this.rrwebRecord.takeFullSnapshot === 'function') {
                    this.rrwebRecord.takeFullSnapshot();
                    logDebug('✅ FullSnapshot taken after returning from idle');
                }
            } else if (this._isIdle === 'unknown') {
                // First interaction, mark as active
                this._isIdle = false;
            }
        } else if (this._isIdle !== true) {
            // Check if we should go idle (no user interaction for threshold time)
            // Note: This uses 5-minute threshold for idle detection (stops recording)
            // Session timeout uses SESSION_IDLE_TIMEOUT_MS (ends session completely)
            const timeSinceLastActivity = currentTime - this._lastActivityTimestamp;
            if (timeSinceLastActivity > this.IDLE_THRESHOLD_MS) {
                logDebug(`⏸️ Session idle detected (${Math.round(timeSinceLastActivity / 1000)}s since last activity) - stopping background event recording`);
                logDebug(`ℹ️ Session will expire after ${Math.round(this.SESSION_IDLE_TIMEOUT_MS / 60000)} minutes of inactivity (${Math.round((this.SESSION_IDLE_TIMEOUT_MS - timeSinceLastActivity) / 1000)}s remaining)`);
                this._isIdle = true;
                
                // Flush buffer when going idle to save what we have
                this.flushEvents();
            }
        }
    }
    
    /**
     * Add an event to the session recording queue
     * These are typically FullSnapshots or IncrementalSnapshots
     */
    public async addRecordingEvent(event: any) {
        // ✅ NON-BLOCKING: Recording events work immediately
        // endUserId and sessionId are already available locally
        
        // ✅ CHECK SESSION TIMEOUT before adding event (creates new session if expired)
        if (isBrowser) {
            this.checkAndRefreshSession();
        }
        
        // ✅ DIRECT EVENT HANDLING - No custom processing to avoid corruption
        // Events flow directly from rrweb to ingestion server
        
        // ✅ EVENT VALIDATION
        if (!event || typeof event !== 'object') {
            logDebug('⚠️ Skipping invalid recording event:', event);
            return;
        }
        
        // ✅ IDLE DETECTION: Update idle state based on event
        this.updateIdleState(event);
        
        // ✅ IDLE STATE: Skip non-interactive events when idle (save bandwidth)
        // Always record FullSnapshots and user interactions, but skip background mutations
        if (this._isIdle === true && event.type === 3 && !this.isInteractiveEvent(event)) {
            // Skip background DOM mutations while idle
            return;
        }
        
        // ✅ LOG FULLSNAPSHOT STATUS FOR DEBUGGING
        if (event.type === 2) { // FullSnapshot
            const hasData = !!event.data;
            const hasNode = !!(event.data && event.data.node);
            
            if (!hasData || !hasNode) {
                logDebug(`⚠️ Empty FullSnapshot detected: hasData=${hasData}, hasNode=${hasNode} - continuing session`);
            } else {
                logDebug(`✅ Valid FullSnapshot: hasData=${hasData}, hasNode=${hasNode}, dataType=${event.data?.node?.type}`);
            }
        }
        
        // Use the same unified queue for all events
        // Queue size management with immediate flushing
        if (this.eventQueue.length >= this.MAX_QUEUE_SIZE) {
            // Drop oldest event when queue is full
            this.eventQueue.shift();
            logDebug('Queue is full, the oldest event is dropped.');
        }
        
        this.eventQueue.push(event); // Direct event handling
        
        // Immediate flush for FullSnapshots (important events)
        if (event.type === 2) { // FullSnapshot
            logDebug('FullSnapshot added, triggering immediate flush');
            this.flushEvents();
        }
        // Immediate flush if queue is getting large (but not when idle)
        else if (this._isIdle !== true && this.eventQueue.length >= this.MAX_QUEUE_SIZE * 0.8) {
            logDebug(`Queue at ${this.eventQueue.length}/${this.MAX_QUEUE_SIZE}, triggering immediate flush`);
            this.flushEvents();
        }
    }



    /**
     * Check if sessionStorage is available and can be used
     */
    private _canUseSessionStorage(): boolean {
        if (!isBrowser) return false;
        try {
            const test = '__sessionStorage_test__';
            sessionStorage.setItem(test, test);
            sessionStorage.removeItem(test);
            return true;
        } catch {
            return false;
        }
    }

    private _canUseLocalStorage(): boolean {
        if (!isBrowser) return false;
        try {
            const test = '__localStorage_test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Get windowId from sessionStorage
     * SessionStorage persists across page reloads but is unique per window/tab
     */
    private _getWindowIdFromStorage(): string | null {
        if (!this._canUseSessionStorage()) {
            return null;
        }
        try {
            return sessionStorage.getItem(this._window_id_storage_key);
        } catch {
            return null;
        }
    }

    /**
     * Set windowId in sessionStorage
     */
    private _setWindowIdInStorage(windowId: string): void {
        if (!this._canUseSessionStorage()) {
            return;
        }
        try {
            sessionStorage.setItem(this._window_id_storage_key, windowId);
            logDebug(`Stored windowId in sessionStorage: ${windowId}`);
        } catch (error) {
            logWarn('Failed to store windowId in sessionStorage:', error);
        }
    }

    /**
     * Remove windowId from sessionStorage
     */
    private _removeWindowIdFromStorage(): void {
        if (!this._canUseSessionStorage()) {
            return;
        }
        try {
            sessionStorage.removeItem(this._window_id_storage_key);
        } catch (error) {
            logWarn('Failed to remove windowId from sessionStorage:', error);
        }
    }

    /**
     * Check if primary_window_exists flag is set in sessionStorage
     * This flag indicates if a window was opened as a new tab/window (not a reload)
     */
    private _getPrimaryWindowExists(): boolean {
        if (!this._canUseSessionStorage()) {
            return false;
        }
        try {
            return sessionStorage.getItem(this._primary_window_exists_storage_key) === 'true';
        } catch {
            return false;
        }
    }

    /**
     * Set primary_window_exists flag in sessionStorage
     * This flag is set when DOM loads and cleared on beforeunload
     */
    private _setPrimaryWindowExists(value: boolean): void {
        if (!this._canUseSessionStorage()) {
            return;
        }
        try {
            if (value) {
                sessionStorage.setItem(this._primary_window_exists_storage_key, 'true');
            } else {
                sessionStorage.removeItem(this._primary_window_exists_storage_key);
            }
        } catch (error) {
            logWarn('Failed to set primary_window_exists flag:', error);
        }
    }

    /**
     * Get or create windowId with multi-window detection
     * - Reuses windowId on page reload (same tab)
     * - Creates new windowId for new windows/tabs
     */
    private getOrCreateWindowId(): string {
        if (!isBrowser) {
            return uuidv1();
        }

        const lastWindowId = this._getWindowIdFromStorage();
        const primaryWindowExists = this._getPrimaryWindowExists();

        if (lastWindowId && !primaryWindowExists) {
            // Page reload: primary_window_exists was cleared on beforeunload
            // Reuse the windowId from sessionStorage
            logDebug(`Reusing windowId from previous page load: ${lastWindowId}`);
            this._setWindowIdInStorage(lastWindowId);
            this._setPrimaryWindowExists(true);
            return lastWindowId;
        } else {
            // New window/tab: primary_window_exists exists (copied from original window)
            // OR no previous windowId exists
            // Create a new windowId
            const newWindowId = uuidv1();
            logDebug(`Creating new windowId: ${newWindowId} (new window/tab detected)`);
            this._setWindowIdInStorage(newWindowId);
            this._setPrimaryWindowExists(true);
            return newWindowId;
        }
    }

    /**
     * Setup beforeunload listener to clear primary_window_exists flag
     * This allows us to distinguish page reloads from new windows/tabs
     */
    private setupWindowUnloadListener(): void {
        if (!isBrowser) {
            return;
        }

        // Use beforeunload to clear the flag before page unloads
        window.addEventListener('beforeunload', () => {
            if (this._canUseSessionStorage()) {
                this._setPrimaryWindowExists(false);
                logDebug('Cleared primary_window_exists flag on beforeunload');
            }
        }, { capture: false });
    }

    // Add helper methods for cookie management with localStorage fallback
    private setCookie(name: string, value: string, daysToExpire: number) {
        if (!isBrowser) return;
        
        try {
            // Try to set cookie first
            const date = new Date();
            date.setTime(date.getTime() + (daysToExpire * 24 * 60 * 60 * 1000));
            const expires = `expires=${date.toUTCString()}`;
            document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
            
            // Also store in localStorage as backup
            localStorage.setItem(name, value);
            logDebug(`Set cookie and localStorage: ${name}`);
        } catch (error) {
            // If cookie fails, use localStorage only
            try {
                localStorage.setItem(name, value);
                logDebug(`Cookie blocked, using localStorage: ${name}`);
            } catch (localStorageError) {
                logError('Failed to store user ID in both cookie and localStorage:', localStorageError);
            }
        }
    }

    public getCookie(name: string): string | null {
        if (!isBrowser) return null;
        
        try {
            // Try to get from cookie first
            const nameEQ = name + "=";
            const ca = document.cookie.split(';');
            for (let i = 0; i < ca.length; i++) {
                let c = ca[i];
                while (c.charAt(0) === ' ') c = c.substring(1, c.length);
                if (c.indexOf(nameEQ) === 0) {
                    const cookieValue = c.substring(nameEQ.length, c.length);
                    logDebug(`Found cookie: ${name}`);
                    return cookieValue;
                }
            }
            
            // If cookie not found, try localStorage
            const localStorageValue = localStorage.getItem(name);
            if (localStorageValue) {
                logDebug(`Cookie not found, using localStorage: ${name}`);
                return localStorageValue;
            }
            
            return null;
        } catch (error) {
            // If cookie access fails, try localStorage
            try {
                const localStorageValue = localStorage.getItem(name);
                if (localStorageValue) {
                    logDebug(`Cookie access failed, using localStorage: ${name}`);
                    return localStorageValue;
                }
            } catch (localStorageError) {
                logError('Failed to access both cookie and localStorage:', localStorageError);
            }
            return null;
        }
    }

    /**
     * Delete a cookie by setting its expiration date to the past
     * @param name The name of the cookie to delete
     */
    private deleteCookie(name: string) {
        if (!isBrowser) return;
        
        try {
            // Delete cookie by setting expiration to past
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
            logDebug(`Deleted cookie: ${name}`);
        } catch (error) {
            logError(`Failed to delete cookie: ${name}`, error);
        }
        
        // Also remove from localStorage
        try {
            localStorage.removeItem(name);
            logDebug(`Removed from localStorage: ${name}`);
        } catch (error) {
            logError(`Failed to remove from localStorage: ${name}`, error);
        }
    }

    /**
     * Clear user data and reset session when user signs out of the site
     * This should be called when a user logs out of your application to prevent
     * data contamination between different users
     */
    public logout(): void {
        if (!isBrowser) return;
        
        try {      
            // Clear user ID cookie and localStorage
            const userIdCookieName = `human_behavior_end_user_id`;
            this.deleteCookie(userIdCookieName);
            
            // Clear the shared session blob (localStorage) plus any per-tab
            // sessionStorage copy from older SDK builds.
            const sessionKey = `human_behavior_session`;
            this._clearStoredSession(sessionKey);
            
            // Reset user-related properties
            this.endUserId = null;
            this.userProperties = {};
            
            // Generate new IDs for the next user
            this.endUserId = uuidv1();
            this.setCookie(`human_behavior_end_user_id`, this.endUserId, 365);
            this.sessionId = this.createNewSession(sessionKey);
            // Create new windowId for new session (logout = new session)
            this.windowId = uuidv1();
            this._setWindowIdInStorage(this.windowId);
            this.api.setTrackingContext(this.sessionId, this.endUserId);
            // Emit FullSnapshot so the player can replay this new window
            this.takeFullSnapshot();

            logInfo('User logged out - cleared all user data and started fresh session');
        } catch (error) {
            logError('Error during logout:', error);
        }
    }

    /**
     * Start redaction functionality for sensitive input fields
     * @param options Optional configuration for redaction behavior
     */
    public async redact(options?: RedactionOptions): Promise<void> {
        await this.ensureInitialized();
        if (!isBrowser) {
            logWarn('Redaction is only available in browser environments');
            return;
        }
        
        // Create a new redaction manager with the provided options
        this.redactionManager = new RedactionManager(options);
    }

    /**
     * Set specific fields to be redacted (for visibility-first mode)
     * @param fields Array of CSS selectors for fields to redact
     */
    public setRedactedFields(fields: string[]): void {
        this.redactionManager.setFieldsToRedact(fields);
        
        // ✅ RESTART RECORDING WITH NEW SETTINGS - Ensures redaction is applied
        if (this.recordInstance) {
            this.restartWithNewRedaction();
        }
    }

    /**
     * Set specific fields to be unredacted (everything else stays redacted by rrweb)
     * @param fields Array of CSS selectors for fields to unredact (e.g., ['#username', '#comment'])
     */
    public setUnredactedFields(fields: string[]): void {
        this.redactionManager.setFieldsToUnredact(fields);
        
        // ✅ RESTART RECORDING WITH NEW SETTINGS - Ensures unredaction is applied
        if (this.recordInstance) {
            this.restartWithNewRedaction();
        }
    }

    private restartWithNewRedaction(): void {
        if (this.recordInstance) {
            this.recordInstance(); // Stop current recording
            this.start(); // Restart with new redaction settings
        }
    }

    /**
     * Check if any fields are currently unredacted
     */
    public hasUnredactedFields(): boolean {
        return this.redactionManager.hasUnredactedFields();
    }

    /**
     * Get the currently unredacted fields
     */
    public getUnredactedFields(): string[] {
        return this.redactionManager.getUnredactedFields();
    }

    /**
     * Remove specific fields from unredaction (they become redacted again)
     * @param fields Array of CSS selectors for fields to redact
     */
    public redactFields(fields: string[]): void {
        this.redactionManager.redactFields(fields);
        
        // ✅ RESTART RECORDING WITH NEW SETTINGS - Ensures redaction is updated
        if (this.recordInstance) {
            this.restartWithNewRedaction();
        }
    }

    /**
     * Clear all unredacted fields (everything becomes redacted again)
     */
    public clearUnredactedFields(): void {
        this.redactionManager.clearUnredactedFields();
        
        // ✅ RESTART RECORDING WITH NEW SETTINGS - Ensures redaction is updated
        if (this.recordInstance) {
            this.restartWithNewRedaction();
        }
    }

    /**
     * Check and refresh session if expired (called before adding events)
     * Uses in-memory state as source of truth
     */
    private checkAndRefreshSession(): void {
        if (!isBrowser) return;
        
        const sessionKey = `human_behavior_session`;
        const now = Date.now();

        // Get session data (checks memory first, then localStorage)
        // getStoredSession() now handles session expiration and creation atomically
        const stored = this.getStoredSession(sessionKey);

        if (!stored || !stored.sessionId) {
            // No stored session - create new one
            this.createNewSession(sessionKey);
            // New session = new windowId
            this.windowId = uuidv1();
            this._setWindowIdInStorage(this.windowId);
            this.api.setTrackingContext(this.sessionId, this.endUserId);
            // Emit FullSnapshot so the player can replay this new window
            this.takeFullSnapshot();
            logDebug(`Created new session (no stored session): ${this.sessionId}`);
            return;
        }

        // Session is valid (getStoredSession() already handled expiration)
        // Update activity timestamp to extend the session
        this.updateSessionActivity(sessionKey, now, stored.sessionId, stored.sessionStartTimestamp);
    }

    /**
     * Get or create session ID with timeout checking
     * Called once during initialization
     * Uses in-memory state as source of truth
     *
     * Session identity is shared across browser tabs (localStorage), matching
     * GA/PostHog: a new tab joins the live session and records under its own
     * `windowId`; the player groups per-window recordings inside the session.
     */
    private getOrCreateSessionId(): string {
        if (!isBrowser) {
            return uuidv1();
        }

        const sessionKey = `human_behavior_session`;
        const now = Date.now();

        // Get session data (checks memory first, then shared localStorage)
        const stored = this.getStoredSession(sessionKey);

        if (!stored || !stored.sessionId) {
            const newSessionId = this.createNewSession(sessionKey);
            this.api.setTrackingContext(newSessionId, this.endUserId);
            return newSessionId;
        }

        const timeSinceActivity = now - stored.lastActivityTimestamp;
        const sessionAge = now - stored.sessionStartTimestamp;

        if (
            timeSinceActivity > this.SESSION_IDLE_TIMEOUT_MS ||
            sessionAge > this.SESSION_MAX_LENGTH_MS
        ) {
            logDebug(`Session expired: idle=${timeSinceActivity}ms, age=${sessionAge}ms`);
            const newSessionId = this.createNewSession(sessionKey);
            this.api.setTrackingContext(newSessionId, this.endUserId);
            return newSessionId;
        }

        // Update activity timestamp (extends session)
        // Memory is already updated by getStoredSession() if it read from storage
        this.updateSessionActivity(sessionKey, now, stored.sessionId, stored.sessionStartTimestamp);
        return stored.sessionId;
    }

    /**
     * Get session data (check memory first, then tab-scoped sessionStorage)
     */
    private getStoredSession(key: string): { sessionId: string; lastActivityTimestamp: number; sessionStartTimestamp: number } | null {
        const now = Date.now();
        const SESSION_IDLE_TIMEOUT_MS = this.SESSION_IDLE_TIMEOUT_MS;
        const SESSION_MAX_LENGTH_MS = this.SESSION_MAX_LENGTH_MS;
        
        // Check in-memory state first (source of truth during session)
        // BUT: Always validate expiration before returning from memory
        if (this.sessionId && this._sessionActivityTimestamp !== null && this._sessionStartTimestamp !== null) {
            const timeSinceActivity = now - this._sessionActivityTimestamp;
            const sessionAge = now - this._sessionStartTimestamp;
            
            // If expired, immediately create new session (atomic operation)
            if (timeSinceActivity > SESSION_IDLE_TIMEOUT_MS || sessionAge > SESSION_MAX_LENGTH_MS) {
                logDebug(`Session in memory expired: adopting shared session or creating a new one`);
                const oldSessionId = this.sessionId;
                const adopted = this._adoptOrCreateSession(key);
                if (!adopted) {
                    // New session = new windowId (an adopted session keeps this
                    // tab's windowId; the FullSnapshot below re-anchors it).
                    this.windowId = uuidv1();
                    this._setWindowIdInStorage(this.windowId);
                }
                this.api.setTrackingContext(this.sessionId, this.endUserId);
                // Emit FullSnapshot so the player can replay this new window
                this.takeFullSnapshot();
                logInfo(`🔄 Session timeout (memory): Created new session ${this.sessionId} (previous: ${oldSessionId})`);
                // Return the new session (createNewSession ensures these are not null)
                if (this._sessionActivityTimestamp !== null && this._sessionStartTimestamp !== null) {
                    return {
                        sessionId: this.sessionId,
                        lastActivityTimestamp: this._sessionActivityTimestamp,
                        sessionStartTimestamp: this._sessionStartTimestamp
                    };
                }
            } else {
                // Session in memory is valid
                return {
                    sessionId: this.sessionId,
                    lastActivityTimestamp: this._sessionActivityTimestamp,
                    sessionStartTimestamp: this._sessionStartTimestamp
                };
            }
        }
        
        // Only read from shared storage if memory is empty (init or reload)
        try {
            const stored = this._readStoredSessionRaw(key);
            if (!stored) return null;
            const parsed = JSON.parse(stored);
            
            // Check expiration BEFORE setting in memory
            const timeSinceActivity = now - parsed.lastActivityTimestamp;
            const sessionAge = now - parsed.sessionStartTimestamp;
            
            if (timeSinceActivity > SESSION_IDLE_TIMEOUT_MS || sessionAge > SESSION_MAX_LENGTH_MS) {
                // Session expired - immediately create new session (atomic operation)
                logDebug(`Stored session expired: idle=${Math.round(timeSinceActivity / 1000 / 60)}min, age=${Math.round(sessionAge / 1000 / 60 / 60)}hrs`);
                const oldSessionId = parsed.sessionId;
                this.createNewSession(key);
                // New session = new windowId
                this.windowId = uuidv1();
                this._setWindowIdInStorage(this.windowId);
                this.api.setTrackingContext(this.sessionId, this.endUserId);
                // Emit FullSnapshot so the player can replay this new window
                this.takeFullSnapshot();
                logInfo(`🔄 Session timeout (storage): Created new session ${this.sessionId} (previous: ${oldSessionId})`);
                // Return the new session (createNewSession ensures these are not null)
                if (this._sessionActivityTimestamp !== null && this._sessionStartTimestamp !== null) {
                    return {
                        sessionId: this.sessionId,
                        lastActivityTimestamp: this._sessionActivityTimestamp,
                        sessionStartTimestamp: this._sessionStartTimestamp
                    };
                }
            }
            
            // Session is valid - update memory from storage (for next time)
            if (parsed.sessionId) {
                this.sessionId = parsed.sessionId;
                this._sessionActivityTimestamp = parsed.lastActivityTimestamp;
                this._sessionStartTimestamp = parsed.sessionStartTimestamp;
            }
            
            return parsed;
        } catch {
            return null;
        }
    }

    /**
     * Create a new session (update memory first, then persistence)
     */
    private createNewSession(key: string): string {
        const sessionId = uuidv1();
        const now = Date.now();
        
        // Update memory immediately (source of truth)
        this.sessionId = sessionId;
        this._sessionActivityTimestamp = now;
        this._sessionStartTimestamp = now;
        
        // ✅ Sync idle detection timestamp with session activity timestamp
        // This ensures idle detection (5 min) and session timeout (SESSION_IDLE_TIMEOUT_MS) are aligned
        this._lastActivityTimestamp = now;
        
        // Then write to shared persistence
        const session = {
            sessionId,
            lastActivityTimestamp: now,
            sessionStartTimestamp: now
        };
        this._writeStoredSession(key, session);
        
        logDebug(`Created new session: ${sessionId}`);
        return sessionId;
    }

    /**
     * Update session activity timestamp (update memory first, then persistence)
     * Note: This is called by checkAndRefreshSession() for any event, not just user interactions
     * For user interactions, updateIdleState() also updates this, keeping them in sync
     */
    private updateSessionActivity(key: string, timestamp: number, sessionId: string, sessionStartTimestamp: number): void {
        // Update memory immediately (source of truth)
        this.sessionId = sessionId;
        this._sessionActivityTimestamp = timestamp;
        this._sessionStartTimestamp = sessionStartTimestamp;
        
        // ✅ Note: We don't update _lastActivityTimestamp here because:
        // - updateIdleState() handles it for user interactions
        // - Non-interactive events shouldn't reset idle detection (5 min threshold)
        // - Session timeout (SESSION_IDLE_TIMEOUT_MS) is extended by any event via checkAndRefreshSession()
        
        // Then write to shared persistence
        const session = {
            sessionId,
            lastActivityTimestamp: timestamp,
            sessionStartTimestamp
        };
        this._writeStoredSession(key, session);
    }

    /**
     * If another tab kept (or rotated) the shared session while this tab's
     * in-memory copy went stale, adopt it instead of minting a competitor.
     * Returns true when an existing shared session was adopted.
     */
    private _adoptOrCreateSession(key: string): boolean {
        const now = Date.now();
        try {
            const raw = this._readStoredSessionRaw(key);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (
                    isAdoptableSharedSession(
                        parsed,
                        this.sessionId,
                        now,
                        this.SESSION_IDLE_TIMEOUT_MS,
                        this.SESSION_MAX_LENGTH_MS,
                    )
                ) {
                    this.sessionId = parsed.sessionId;
                    this._sessionActivityTimestamp = parsed.lastActivityTimestamp;
                    this._sessionStartTimestamp = parsed.sessionStartTimestamp;
                    logDebug(`Adopted shared session from another tab: ${parsed.sessionId}`);
                    return true;
                }
            }
        } catch {
            // fall through to a fresh session
        }
        this.createNewSession(key);
        return false;
    }

    /** Shared session blob (localStorage). Concurrent tabs share one session. */
    private _writeStoredSession(
        key: string,
        session: { sessionId: string; lastActivityTimestamp: number; sessionStartTimestamp: number },
    ): void {
        const raw = JSON.stringify(session);
        try {
            if (this._canUseLocalStorage()) {
                localStorage.setItem(key, raw);
                return;
            }
        } catch (e) {
            logWarn(`Failed to save session to localStorage: ${e}`);
        }
        // Fallback for environments without localStorage (session stays per-tab).
        try {
            if (this._canUseSessionStorage()) {
                sessionStorage.setItem(key, raw);
            }
        } catch (e) {
            logWarn(`Failed to save session to sessionStorage: ${e}`);
        }
    }

    private _readStoredSessionRaw(key: string): string | null {
        try {
            if (this._canUseLocalStorage()) {
                const shared = localStorage.getItem(key);
                if (shared) return shared;
            }
        } catch {
            // fall through
        }
        // Per-tab copy written by older SDK builds (or the fallback above).
        try {
            if (this._canUseSessionStorage()) {
                const fromTab = sessionStorage.getItem(key);
                if (fromTab) return fromTab;
            }
        } catch {
            // fall through
        }
        return null;
    }

    private _clearStoredSession(key: string): void {
        try {
            if (this._canUseSessionStorage()) {
                sessionStorage.removeItem(key);
            }
        } catch {
            // ignore
        }
        try {
            localStorage.removeItem(key);
        } catch {
            // ignore
        }
    }

    /**
     * Get the current session ID
     */
    public getSessionId(): string {
        return this.sessionId;
    }

    /**
     * Get the current URL being tracked
     */
    public getCurrentUrl(): string {
        // Read the live URL rather than the cached `currentUrl` so callers
        // see the same value `window.location.href` would return — useful
        // when the SDK's own navigation tracker hasn't fired yet (e.g.
        // immediately after a programmatic pushState in user code).
        if (isBrowser && typeof window !== 'undefined' && window.location) {
            return window.location.href;
        }
        return this.currentUrl;
    }

    /**
     * Get current snapshot frequency info
     * Uses configured values (5 minutes, 1000 events)
     */
    public getSnapshotFrequencyInfo(): {
        sessionDuration: number;
        currentInterval: number;
        currentThreshold: number;
        phase: string;
    } {
        const sessionDuration = Date.now() - this.sessionStartTime;
        
        return {
            sessionDuration,
            currentInterval: 300000, // Configured - 5 minutes
            currentThreshold: 1000,  // Configured - 1000 events
            phase: 'configured' // Using explicit configuration
        };
    }

    /**
     * Test if the tracker can reach the ingestion server
     */
    public async testConnection(): Promise<{ success: boolean; error?: string }> {
        try {
            await this.api.init(this.sessionId, this.endUserId);
            return { success: true };
        } catch (error: any) {
            return { 
                success: false, 
                error: error.message || 'Unknown error' 
            };
        }
    }

    /**
     * Get connection status and recommendations
     */
    public getConnectionStatus(): { 
        blocked: boolean; 
        recommendations: string[] 
    } {
        const recommendations: string[] = [];
        let blocked = false;

        // Check if we have queued events (might indicate blocking)
        if (this.eventQueue.length > 0) {
            blocked = true;
            recommendations.push('Some requests may be blocked by ad blockers');
        }

        // Check if connection was blocked during initialization
        if (this._connectionBlocked) {
            blocked = true;
            recommendations.push('Initial connection test failed - ad blocker may be active');
        }

        // Check if we're in a browser environment
        if (typeof window === 'undefined') {
            recommendations.push('Not running in browser environment');
        }

        // Check if navigator.sendBeacon is available
        if (typeof navigator.sendBeacon === 'undefined') {
            recommendations.push('sendBeacon not available, using fetch fallback');
        }

        return { blocked, recommendations };
    }

    /**
     * Check if the current user is a preexisting user
     * Returns true if the user has an existing endUserId cookie from a previous session
     */
    public isPreexistingUser(): boolean {
        if (!isBrowser) {
            return false;
        }
        
        // Check if there's an existing endUserId cookie for this API key
        const existingEndUserId = this.getCookie(`human_behavior_end_user_id`);
        return existingEndUserId !== null && existingEndUserId !== this.endUserId;
    }

    /**
     * Get user information including whether they are preexisting
     */
    public getUserInfo(): {
        endUserId: string | null;
        sessionId: string;
        isPreexistingUser: boolean;
        initialized: boolean;
    } {
        return {
            endUserId: this.endUserId,
            sessionId: this.sessionId,
            isPreexistingUser: this.isPreexistingUser(),
            initialized: this.initialized
        };
    }

    // ===== PROPERTY MANAGEMENT METHODS =====

    /**
     * Set a session property that will be included in all events for this session
     */
    public setSessionProperty(key: string, value: any): void {
        this.propertyManager.setSessionProperty(key, value);
    }

    /**
     * Set multiple session properties
     */
    public setSessionProperties(properties: Record<string, any>): void {
        this.propertyManager.setSessionProperties(properties);
    }

    /**
     * Get a session property
     */
    public getSessionProperty(key: string): any {
        return this.propertyManager.getSessionProperty(key);
    }

    /**
     * Remove a session property
     */
    public removeSessionProperty(key: string): void {
        this.propertyManager.removeSessionProperty(key);
    }

    /**
     * Set a user property that will be included in all events
     */
    public setUserProperty(key: string, value: any): void {
        this.propertyManager.setUserProperty(key, value);
    }

    /**
     * Set multiple user properties
     */
    public setUserProperties(properties: Record<string, any>): void {
        this.propertyManager.setUserProperties(properties);
    }

    /**
     * Get a user property
     */
    public getUserProperty(key: string): any {
        return this.propertyManager.getUserProperty(key);
    }

    /**
     * Remove a user property
     */
    public removeUserProperty(key: string): void {
        this.propertyManager.removeUserProperty(key);
    }

    /**
     * Set a property only if it hasn't been set before
     */
    public setOnce(key: string, value: any, scope: 'session' | 'user' = 'user'): void {
        this.propertyManager.setOnce(key, value, scope);
    }

    /**
     * Clear all session properties
     */
    public clearSessionProperties(): void {
        this.propertyManager.clearSessionProperties();
    }

    /**
     * Clear all user properties
     */
    public clearUserProperties(): void {
        this.propertyManager.clearUserProperties();
    }

    /**
     * Get all properties for debugging
     */
    public getAllProperties(): {
        automatic: Record<string, any>;
        session: Record<string, any>;
        user: Record<string, any>;
        initial: Record<string, any>;
    } {
        return this.propertyManager.getAllProperties();
    }
}

// Only expose to window object in browser environments
if (isBrowser) {
    (window as any).HumanBehaviorTracker = HumanBehaviorTracker;
}

export default HumanBehaviorTracker;
